/**
 * The twenty-one workflow steps, ported from `OEM+_Platform_Demo.html`.
 *
 * Source lines
 * ------------
 * `WFS` — demo lines 3396–3684. Per step:
 *
 * | # | id         | module        | demo line | name                          |
 * |---|------------|---------------|-----------|-------------------------------|
 * | 1 | `reg`      | `installed`   | 3398      | Installed base register       |
 * | 2 | `life`     | `installed`   | 3411      | Machine life & meters         |
 * | 3 | `duty`     | `demand`      | 3426      | Duty & demand signals         |
 * | 4 | `forecast` | `demand`      | 3439      | Predicted parts demand        |
 * | 5 | `intake`   | `orchestrate` | 3450      | Case intake & triage          |
 * | 6 | `assess`   | `diagnose`    | 3462      | Diagnosis & prescription      |
 * | 7 | `rca`      | `diagnose`    | 3475      | Root cause analysis           |
 * | 8 | `remote4`  | `diagnose`    | 3490      | Remote diagnosis              |
 * | 9 | `entitle`  | `commercial`  | 3505      | Entitlement & warranty        |
 * |10 | `value`    | `commercial`  | 3518      | Quote, margin & growth        |
 * |11 | `plan`     | `promise`     | 3531      | Service plan & window         |
 * |12 | `ready`    | `promise`     | 3544      | Promise & readiness           |
 * |13 | `pipe`     | `parts`       | 3559      | Pipeline & expediting         |
 * |14 | `stock`    | `parts`       | 3572      | Stock & replenishment         |
 * |15 | `book`     | `parts`       | 3588      | Parts book & supersessions    |
 * |16 | `crew`     | `capacity`    | 3601      | Capacity & utilisation        |
 * |17 | `skills`   | `capacity`    | 3614      | Skills & certification        |
 * |18 | `pack`     | `execute`     | 3627      | Work package execution        |
 * |19 | `onsite`   | `execute`     | 3642      | On-site time capture          |
 * |20 | `cost`     | `closeout`    | 3657      | Job costing & cost to serve   |
 * |21 | `outcome`  | `closeout`    | 3672      | Customer outcome & CSS        |
 *
 * The block helpers those steps used are demo lines 3386–3394: `wfT(cols,rows)`,
 * `wfC(title,sub,body)`, `wfBar(rows)`, `wfNote(text)`, `plainT(t)`,
 * `mLink(id)`, `bandDot(id)`, `wfSite(m)` and `daysWord(n)`.
 *
 * ── What became static, and what became a function ──────────────────
 *
 * `name`, `tag`, `purpose` and `also` are **static data**, copied verbatim.
 *
 * `kpis` and `blocks` are **pure functions of an explicit
 * {@link WorkflowStepContext}** for **all twenty-one steps**, with no
 * exceptions. Every one of them reads at least one loaded dataset — `FLEET`,
 * `WOS`, `HEALTH`, `SD`, `ALERTS`, `B.mx_pm`, `B.inventory`, `B.css.rows`,
 * `jobs()`, `predictedUsage()`, `revenue()` and so on — so none could be
 * reduced to a static descriptor without freezing this morning's numbers into
 * the seed. (Two steps come close: `intake`'s second KPI is the literal `6`
 * and `cost`'s first is the literal `'15,083'`. Both sit beside three live
 * ones, so the step stays a function and those literals are simply returned.)
 *
 * The only module-scope seed a step reads is `FLEET_RULES` (in `rca`) and
 * `MODULE_BY_ID` (in `intake`, to print the short module name a case was
 * routed to). `FLEET_RULES` belongs to the data lane, so it arrives on the
 * context; `MODULE_BY_ID` is a seed in this directory and is imported directly.
 *
 * **Nothing in this file imports a data module.** The context is passed in.
 *
 * ── The block model ────────────────────────────────────────────────
 *
 * The demo's `blocks()` returned an HTML string. Here it returns a
 * {@link WorkflowBlock}`[]` — a discriminated union a React scene renders as
 * real elements, with no raw-HTML injection anywhere:
 *
 * | demo helper                 | ported block                                   |
 * |-----------------------------|------------------------------------------------|
 * | `wfC(title, sub, body)`     | `{ kind: 'card', title, sub, body }`           |
 * | `wfT(cols, rows)`           | `{ kind: 'table', cols, rows }`                |
 * | `wfBar(rows)`               | `{ kind: 'bars', rows }`                       |
 * | `wfNote(text)`              | `{ kind: 'note', text }`                       |
 * | `<div class="wfroute">…`    | `{ kind: 'routes', routes }`                   |
 * | `<div class="specline">…`   | `{ kind: 'quotes', lines }`                    |
 *
 * `wfT`'s convention that a column label beginning `#` is right-aligned
 * becomes {@link WorkflowColumn.numeric}; the `#` is stripped from the label,
 * exactly as `wfT` did.
 *
 * Cells are {@link WorkflowCell}s rather than HTML fragments:
 *
 * | demo fragment                                | ported cell                             |
 * |----------------------------------------------|-----------------------------------------|
 * | `mLink(id)`                                  | `{ kind: 'machine', id }`               |
 * | `bandDot(id) + mLink(id)`                    | `{ kind: 'machine', id, band: true }`   |
 * | `[…].map(mLink).join(' ')`                   | `{ kind: 'machines', ids }`             |
 * | `<span class="mono">…</span>`                | `{ kind: 'mono', text }`                |
 * | `<span class="pill <tone> plain">…</span>`   | `{ kind: 'pill', text, tone }`          |
 * | `<b style="color:var(--x)">…</b>`            | `{ kind: 'emphasis', text, tone }`      |
 * | the inline planned/unplanned ratio bar       | `{ kind: 'meter', pct }`                |
 * | a `<button class="src">` with a `show(…)` handler | `{ kind: 'link', label, nav }`     |
 * | `<span class="rdy ok/no">✓/✗ …</span>`       | `{ kind: 'check', ok, text }`           |
 *
 * A plain `string` cell is still a plain string. `esc()` is dropped throughout
 * — React escapes for us — and `plainT()` becomes {@link stripTags}.
 *
 * Notes carry inline links the same way: a `{@link WorkflowNote}` is an
 * ordered list of `{ kind: 'text' }` and `{ kind: 'link' }` segments, so the
 * sentence reads exactly as the demo wrote it with the buttons in place.
 *
 * ── Handler strings ────────────────────────────────────────────────
 *
 * Every inline `show('x','y')` handler inside a note, a route or a cell is an
 * {@link SeedNav} built with `navShow`, and every `also` entry likewise. The
 * demo's HTML entity `&amp;` inside those button labels is restored to a plain
 * `&`, since it was an HTML-escaping artefact, not content.
 *
 * ── Truncation and formatting ──────────────────────────────────────
 *
 * The demo truncated several cells with `.slice(0, n)` inside the derivation.
 * Those slices are reproduced, because they change what the table shows; the
 * scene lane should treat them as the demo's own column budgets rather than as
 * a styling decision it is free to undo. `toLocaleString()` and `toFixed()`
 * calls are likewise reproduced verbatim.
 *
 * ── Time ───────────────────────────────────────────────────────────
 *
 * No `Date.now()`, no zero-argument `new Date()`, no `Math.random()`. The
 * demo's `daysTo(d)` compares against its frozen `TODAY`; it arrives on the
 * context as {@link WorkflowHelpers.daysTo} so this file never reads a clock.
 *
 * Data Schema layer — pure types and pure functions. No React, no side
 * effects, no imports from `modules/`.
 */

import type { Alert } from './alerts';
import { MODULE_BY_ID, navShow, type ModuleId, type SeedNav } from './modules';

/* ================================================================== *
 * Blocks
 * ================================================================== */

/** Pill tones the demo used inside workflow tables. */
export type WorkflowPillTone = 'good' | 'warn' | 'mute' | 'vpoor' | 'fair';

/** Colour tokens the demo emphasised a number with. */
export type WorkflowEmphasisTone = 'warn' | 'good' | 'fair' | 'poor' | 'vpoor';

/** Condition bands. Demo line 1867 — `const STL={…}`. */
export type HealthBand = 'major' | 'vpoor' | 'poor' | 'fair' | 'good';

/** One segment of a note sentence. */
export type WorkflowNoteSegment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'link'; readonly label: string; readonly nav: SeedNav };

/** A `wfNote(…)` sentence: literal text with inline links. */
export type WorkflowNote = readonly WorkflowNoteSegment[];

/** One cell of a `wfT(…)` table. */
export type WorkflowCell =
  | string
  /** `mLink(id)`, optionally preceded by `bandDot(id)`. */
  | { readonly kind: 'machine'; readonly id: string; readonly band?: true }
  /** Several `mLink`s joined by a space. */
  | { readonly kind: 'machines'; readonly ids: readonly string[] }
  /** `<span class="mono">`. */
  | { readonly kind: 'mono'; readonly text: string }
  /** `<span class="pill <tone> plain">`. */
  | { readonly kind: 'pill'; readonly text: string; readonly tone: WorkflowPillTone }
  /** `<b style="color:var(--tone)">`. */
  | {
      readonly kind: 'emphasis';
      readonly text: string;
      readonly tone?: WorkflowEmphasisTone;
    }
  /** The inline planned-vs-unplanned ratio bar in `plan`. */
  | { readonly kind: 'meter'; readonly pct: number }
  /** A `<button class="src">` inside a cell. */
  | { readonly kind: 'link'; readonly label: string; readonly nav: SeedNav }
  /** The readiness ticks in `ready` — `✓`/`✗` plus a phrase. */
  | { readonly kind: 'check'; readonly ok: boolean; readonly text: string }
  /** Dimmed inline text, the demo's `<span class="dm">`. */
  | { readonly kind: 'dim'; readonly text: string };

/** One column header of a `wfT(…)` table. */
export interface WorkflowColumn {
  /** Header text, with `wfT`'s leading `#` already stripped. */
  readonly label: string;
  /** The `#` prefix — the demo right-aligned these. */
  readonly numeric?: true;
}

/** One row of a `wfBar(…)` chart. */
export interface WorkflowBar {
  /** Row label. */
  readonly label: string;
  /** The value the bar width is scaled from. */
  readonly value: number;
  /** The text printed at the end of the row (`txt ?? String(value)`). */
  readonly text: string;
  /** Optional bar colour, e.g. `'var(--vpoor)'`. */
  readonly colour?: string;
}

/** One panel of the `wfroute` grid. */
export interface WorkflowRoute {
  /** Bold heading. */
  readonly title: string;
  /** The sentence under it. */
  readonly text: string;
  /** The button, when the demo drew one. */
  readonly link?: { readonly label: string; readonly nav: SeedNav };
}

/** One `specline` customer comment in the `outcome` step. */
export interface WorkflowQuote {
  /** Site, printed bold. */
  readonly site: string;
  /** Customer contact. */
  readonly contact: string;
  /** Machine id, or `'—'` when the response carried none. */
  readonly machine: string;
  /** The comment, printed inside curly quotes. */
  readonly comment: string;
}

/** A renderable block of a workflow step. */
export type WorkflowBlock =
  | {
      readonly kind: 'card';
      readonly title: string;
      readonly sub: string;
      readonly body: readonly WorkflowBlock[];
    }
  | {
      readonly kind: 'table';
      readonly cols: readonly WorkflowColumn[];
      readonly rows: readonly (readonly WorkflowCell[])[];
    }
  | { readonly kind: 'bars'; readonly rows: readonly WorkflowBar[] }
  | { readonly kind: 'note'; readonly text: WorkflowNote }
  | { readonly kind: 'routes'; readonly routes: readonly WorkflowRoute[] }
  | { readonly kind: 'quotes'; readonly lines: readonly WorkflowQuote[] };

/** One KPI tile in the step hero — the demo's `[eyebrow, value, detail]`. */
export interface WorkflowKpi {
  /** The small label above the number. */
  readonly eyebrow: string;
  /** The number or short string, already formatted as the demo formatted it. */
  readonly value: string;
  /** The line under it. */
  readonly detail: string;
}

/* ---- block constructors, mirroring the demo helpers ---- */

/** `wfC(title, sub, body)`. */
export const wfCard = (
  title: string,
  sub: string,
  body: readonly WorkflowBlock[],
): WorkflowBlock => ({ kind: 'card', title, sub, body });

/**
 * `wfT(cols, rows)`.
 *
 * `cols` are given exactly as the demo wrote them — a leading `#` marks a
 * right-aligned column and is stripped from the label, as `wfT` did.
 */
export const wfTable = (
  cols: readonly string[],
  rows: readonly (readonly WorkflowCell[])[],
): WorkflowBlock => ({
  kind: 'table',
  cols: cols.map((c) =>
    c.startsWith('#') ? { label: c.slice(1), numeric: true as const } : { label: c },
  ),
  rows,
});

/** `wfBar(rows)`. */
export const wfBars = (rows: readonly WorkflowBar[]): WorkflowBlock => ({ kind: 'bars', rows });

/** `wfNote(text)`. */
export const wfNote = (text: WorkflowNote): WorkflowBlock => ({ kind: 'note', text });

/** A literal piece of a note. */
export const nt = (text: string): WorkflowNoteSegment => ({ kind: 'text', text });

/** A `<button class="src">` inside a note. */
export const nlink = (label: string, nav: SeedNav): WorkflowNoteSegment => ({
  kind: 'link',
  label,
  nav,
});

/** `mLink(id)`, with the leading `bandDot(id)` the demo often prefixed. */
export const mcell = (id: string, band?: true): WorkflowCell =>
  band === undefined ? { kind: 'machine', id } : { kind: 'machine', id, band };

/** `<span class="mono">`. */
export const mono = (text: string): WorkflowCell => ({ kind: 'mono', text });

/** `plainT(t)` — demo line 3389, minus the `esc()` React does for us. */
export const stripTags = (value: unknown): string => String(value).replace(/<[^>]*>/g, '');

/** `daysWord(n)` — demo line 3395. Pure; no clock is read. */
export const daysWord = (n: number): string =>
  n < 0 ? `${-n} d overdue` : n === 0 ? 'today' : `${n} d`;

/* ================================================================== *
 * Row shapes
 *
 * Minimal structural interfaces: exactly the fields the twenty-one steps
 * read, no more. Where the demo's data pack uses a positional row (the
 * spreadsheet-shaped tables), the tuple is reproduced with labelled elements
 * so the index the demo used stays legible.
 * ================================================================== */

/** A row of `FLEET`. */
export interface FleetMachine {
  readonly machine_id: string;
  readonly model: string;
  /** Mine code, e.g. `'KG'`. */
  readonly mine: string;
  readonly subclass: string;
  readonly engine_hours: number;
}

/** A value of `CLASSES_`. */
export interface MachineClass {
  readonly name?: string;
  readonly subclass?: string;
  readonly drive?: string;
}

/** A value of `MINES`. */
export interface MineInfo {
  readonly id: string;
  readonly name: string;
  readonly state: string;
  /** `'Live'` or the shift-end mode. */
  readonly data_mode: string;
  readonly labour_rate: number;
  readonly mining?: string;
}

/** A row of `HEALTH`. */
export interface ComponentHealth {
  readonly machine_id: string;
  readonly component: string;
  readonly health_score: number;
  readonly band: HealthBand;
  readonly hours_since_change: number;
}

/** A row of `WOS`. */
export interface WorkOrder {
  readonly wo_id: string;
  readonly mine: string;
  readonly machine_id: string;
  readonly wo_type: string;
  readonly priority: string;
  readonly status: string;
  readonly created: string;
  readonly component?: string;
  readonly description?: string;
  readonly total_direct_cost_aud: number;
}

/** A row of `SD` — the shift record. */
export interface ShiftDay {
  readonly date: string;
  readonly machine_id: string;
  readonly hours: number;
  readonly alarms: number;
  readonly diesel_l: number;
  /** Semicolon-separated delay codes, or empty. */
  readonly delays?: string;
}

/**
 * Alerts come from the canonical seed, not a local restatement.
 *
 * This file originally declared its own tuple-shaped `Alert`, because the two
 * steps that read alerts only ever indexed into the chain and evidence arrays
 * positionally. `alerts.ts` models both as named-field objects, and a context
 * built from `ALERTS` could not satisfy a tuple-shaped contract — so the
 * canonical types are re-exported here and the readers use the field names.
 */
export type { Alert, AlertChainStep, AlertEvidence } from './alerts';

/** A row of `jobs()` — the open-case board. */
export interface CaseJob {
  readonly id: string;
  readonly kind: string;
  readonly title: string;
  readonly owner: string;
  /** Module id the case has been routed to. */
  readonly stage: string;
  readonly machine?: string;
  readonly mine?: string;
  readonly sev?: string;
  readonly next?: string;
}

/** A row of `FLEET_RULES` — demo line 2821. `[rule, from, appliesTo]`. */
export type FleetRule = readonly [rule: string, from: string, appliesTo: string];

/** A Maximo asset record — `mxOf(id)`. */
export interface MaximoAsset {
  readonly installdate?: string;
  readonly serialnum?: string;
  readonly warranty?: { readonly status?: string };
  readonly servicecontract?: {
    readonly contractnum?: string;
    readonly coverage?: string;
    readonly type?: string;
  };
}

/** A row of `B.mx_contracts`. */
export interface MaximoContract {
  readonly contractnum: string;
  readonly customer: string;
  readonly type: string;
  readonly machines: number;
  readonly labour_rate_aud_h: number;
  readonly end: string;
  readonly contractor?: string;
  readonly coverage?: string;
}

/** A row of `B.mx_pm` — the preventive-maintenance schedule. */
export interface MaximoPm {
  readonly assetnum: string;
  readonly pmnum: string;
  readonly description: string;
  readonly nextdue_date: string;
  readonly leadtime_days: number;
  readonly frequency: number;
  readonly frequnit: string;
  readonly jobplan: string;
}

/** A row of `B.inventory`. */
export interface InventoryLine {
  readonly location: string;
  readonly part_no: string;
  readonly qty_on_hand: number;
  readonly min_level: number;
}

/** A row of `B.parts`. */
export interface CataloguePart {
  readonly part_no: string;
  readonly description: string;
  readonly system: string;
  readonly list_price_aud: number;
  readonly lead_time_days: number;
  readonly criticality: string;
  readonly fits: string;
}

/** A row of `B.orders`. */
export interface PartOrder {
  readonly order_no: string;
  readonly order_date: string;
  readonly part_no: string;
  readonly qty?: number;
  readonly machine_id?: string;
  readonly mine: string;
  readonly status: string;
  readonly linked_wo?: string;
}

/** A row of `B.cases`. */
export interface ServiceCase {
  readonly case_no: string;
  readonly opened: string;
  readonly machine_id: string;
  readonly mine: string;
  readonly type: string;
  readonly status: string;
  readonly subject: string;
  readonly owner: string;
}

/** A row of `B.bulletins`. */
export interface Bulletin {
  readonly id: string;
  readonly title: string;
  readonly file: string;
}

/** A row of `B.xreach_sessions`. */
export interface RemoteSession {
  /** ISO timestamp with a `T` separator. */
  readonly started: string;
  readonly site: string;
  readonly machine?: string;
  readonly topic: string;
  readonly component?: string;
  readonly expert: string;
  readonly expert_org: string;
  readonly duration_min: number;
  readonly device?: string;
  readonly network?: string;
}

/** A row of `B.tapp_jobs` — the technician app job list. */
export interface TechnicianJob {
  readonly job_id: string;
  readonly wo_ref?: string;
  readonly technician: string;
  readonly machine?: string;
  readonly site: string;
  readonly job_type: string;
  readonly status: string;
  /** ISO timestamp with a `T` separator, when on site. */
  readonly on_site?: string;
}

/** A row of `B.tapp_techs`. */
export interface Technician {
  readonly technician: string;
  /** Mine code of the technician's base. */
  readonly base: string;
  readonly status: string;
  readonly skills: readonly string[];
}

/** A row of `B.timesheets`. */
export interface Timesheet {
  readonly technician: string;
  readonly job_hours: number;
  readonly pm_support_hours: number;
  readonly travel_hours: number;
  readonly standby_hours: number;
  readonly day_off?: boolean;
}

/** A row of `B.roster.rows`. */
export type RosterRow = readonly [
  technician: string,
  role: string,
  baseSite: string,
  roster: string,
  swingStart: string,
  swingEnd: string,
  available: string,
];

/** A row of `B.lms`. */
export interface TrainingRecord {
  readonly person: string;
  readonly org: string;
  readonly role: string;
  readonly course: string;
  readonly completed: string;
  readonly expires: string;
  readonly status: string;
}

/** A row of `B.css.rows` — one customer-satisfaction response. */
export type CssRow = readonly [
  jobId: string,
  woRef: string,
  site: string,
  contact: string,
  technician: string,
  machineId: string,
  date: string,
  quality: number,
  responsiveness: number,
  reporting: number,
  overall: number,
  comment: string,
];

/** A row of `B.events_daily.rows` — one alarm code on one machine on one day. */
export type EventDailyRow = readonly [
  machineId: string,
  date: string,
  code: string,
  severity: string,
  count: number,
];

/** A row of `B.cost_per_machine`. */
export interface CostPerMachine {
  readonly machine_id: string;
  readonly cost_per_op_hour_aud: number;
  readonly amount_aud: number;
  readonly op_hours_18m: number;
}

/**
 * A row of `B.cost_by_machine_cat`.
 *
 * The demo read it defensively — `r.category || r[1]` and
 * `r.amount_aud ?? r[2]` — because the pack ships it either keyed or
 * positional. Both shapes are kept.
 */
export type CostByCategoryRow =
  | { readonly category: string; readonly amount_aud: number }
  | readonly [machineId: string, category: string, amount: number];

/** A value of `B.rates` — how parts reach one mine. */
export interface SiteRate {
  /** Distribution centre name. */
  readonly dc: string;
  readonly freight_days: number;
}

/** A value of `B.siteinfo`. */
export interface SiteInfo {
  readonly access_and_logistics: { readonly roster: string };
  readonly mine_services: { readonly ventilation: string };
  readonly ground_conditions: { readonly seismic: string };
}

/** A row of `B.catbase_bom`. */
export interface CatbaseBomLine {
  readonly model_family: string;
  readonly section: string;
  readonly part_no: string;
  readonly description: string;
  readonly qty_per_assembly: number;
  readonly criticality: string;
  readonly list_price_aud: number;
  readonly lead_time_days: number;
  readonly serial_range: string;
}

/** A row of `B.catbase_sections`. */
export interface CatbaseSection {
  readonly model: string;
  readonly section: string;
  readonly section_title: string;
  readonly part_no: string;
  readonly description: string;
  readonly qty_per_assembly: number;
  readonly note?: string;
}

/** A row of `predictedUsage()`. */
export interface PredictedUsageRow {
  readonly part_no: string;
  readonly desc?: string;
  /** Mine code. */
  readonly mine: string;
  readonly demand: number;
  readonly store?: number;
  readonly dcq?: number;
  readonly gap: number;
  readonly prop?: string;
}

/** A row of `renewals()`. */
export interface ContractRenewal {
  readonly contractnum: string;
  readonly customer: string;
  readonly type: string;
  readonly machines: number;
  readonly end: string;
  readonly days: number;
  readonly contractor?: string;
}

/** The result of `revenue()` — demo line 2839. */
export interface RevenueSummary {
  readonly parts12: number;
  readonly parts18: number;
  readonly labour10w: number;
  readonly freightAll: number;
  readonly freightBd: number;
  readonly reactiveShare: number;
}

/** The result of `techStats(name)` — demo line 2712, fields the steps read. */
export interface TechnicianStats {
  readonly done: number;
  readonly util: number;
  readonly fft: number | null;
  readonly cssAvg: number | null;
}

/* ================================================================== *
 * The context
 * ================================================================== */

/**
 * The demo's `B` object — the loaded data pack, narrowed to the tables the
 * twenty-one steps actually read.
 */
export interface WorkflowDataPack {
  /** `B.mx` — the Maximo service register, keyed by machine id. Used by `reg`. */
  readonly mx: Readonly<Record<string, MaximoAsset>>;
  /** `B.mx_contracts` — used by `reg`, `entitle`. */
  readonly mx_contracts: readonly MaximoContract[];
  /** `B.mx_pm` — used by `forecast`, `plan`. */
  readonly mx_pm: readonly MaximoPm[];
  /** `B.inventory` — used by `forecast`, `ready`, `pipe`, `stock`. */
  readonly inventory: readonly InventoryLine[];
  /** `B.parts` — used by `pipe`, `stock`. */
  readonly parts: readonly CataloguePart[];
  /** `B.orders` — used by `ready`, `pipe`. */
  readonly orders: readonly PartOrder[];
  /** `B.cases` — used by `entitle`. */
  readonly cases: readonly ServiceCase[];
  /** `B.bulletins` — used by `rca`, `pack`. */
  readonly bulletins: readonly Bulletin[];
  /** `B.docs` — only its length, in `pack`. */
  readonly docs: readonly unknown[];
  /** `B.events_daily.rows` — used by `duty`. */
  readonly events_daily: { readonly rows: readonly EventDailyRow[] };
  /** `B.events_critical` — only its length, in `duty`. */
  readonly events_critical: readonly unknown[];
  /** `B.xreach_sessions` — used by `remote4`, `skills`. */
  readonly xreach_sessions: readonly RemoteSession[];
  /** `B.tapp_jobs` — used by `pack`. */
  readonly tapp_jobs: readonly TechnicianJob[];
  /** `B.tapp_techs` — used by `crew`, `ready`, `skills`. */
  readonly tapp_techs: readonly Technician[];
  /** `B.timesheets` — used by `crew`, `onsite`. */
  readonly timesheets: readonly Timesheet[];
  /** `B.roster.rows` — used by `ready`, `crew`. */
  readonly roster: { readonly rows: readonly RosterRow[] };
  /** `B.lms` — used by `skills`. */
  readonly lms: readonly TrainingRecord[];
  /** `B.css.rows` — used by `outcome`. */
  readonly css: { readonly rows: readonly CssRow[] };
  /** `B.cost_per_machine` — used by `life`, `cost`. */
  readonly cost_per_machine: readonly CostPerMachine[];
  /** `B.cost_by_machine_cat` — used by `cost`. */
  readonly cost_by_machine_cat: readonly CostByCategoryRow[];
  /** `B.rates` — used by `pipe`, `crew`. */
  readonly rates: Readonly<Record<string, SiteRate>>;
  /** `B.siteinfo` — used by `plan`. */
  readonly siteinfo: Readonly<Record<string, SiteInfo>>;
  /** `B.catbase_bom` — used by `book`. */
  readonly catbase_bom: readonly CatbaseBomLine[];
  /** `B.catbase_sections` — used by `book`. */
  readonly catbase_sections: readonly CatbaseSection[];
}

/** Results of the demo's derivation helpers, computed once by the caller. */
export interface WorkflowDerived {
  /** `jobs()` — used by `intake`, `entitle`, `ready`. */
  readonly jobs: readonly CaseJob[];
  /** `predictedUsage()` — used by `forecast`, `stock`. */
  readonly predictedUsage: readonly PredictedUsageRow[];
  /** `pastInterval()` — used by `life`. */
  readonly pastInterval: readonly ComponentHealth[];
  /** `warrantyActive()` — used by `reg`, `entitle`. */
  readonly warrantyActive: readonly FleetMachine[];
  /** `upgradeCandidates()` — used by `value`. */
  readonly upgradeCandidates: readonly FleetMachine[];
  /** `renewals()` — used by `value`. */
  readonly renewals: readonly ContractRenewal[];
  /** `revenue()` — used by `value`. */
  readonly revenue: RevenueSummary;
  /** `meanUtil()` — used by `crew`. */
  readonly meanUtil: number;
}

/** The demo's pure lookup and formatting helpers, supplied by the caller. */
export interface WorkflowHelpers {
  /** `mxOf(id)` — the Maximo asset record for a machine. */
  readonly mxOf: (machineId: string) => MaximoAsset | undefined;
  /** `healthOf(id)` — the condition rows for a machine. */
  readonly healthOf: (machineId: string) => readonly ComponentHealth[];
  /** `STATUS(id)` — the machine's worst band. */
  readonly statusOf: (machineId: string) => HealthBand;
  /** `intervalOf(component)` — demo line 1870. */
  readonly intervalOf: (component: string) => number;
  /**
   * `daysTo(date)` — demo line 2823.
   *
   * The demo measured against its frozen `TODAY`, never the wall clock. The
   * caller passes the same frozen comparison in, which is why nothing in this
   * file reads a clock.
   */
  readonly daysTo: (isoDate: string) => number;
  /** `techStats(name)` — demo line 2712. */
  readonly techStats: (technician: string) => TechnicianStats;
  /** `dataModeLabel(mine)` — demo line 1849. */
  readonly dataModeLabel: (mine: MineInfo) => string;
  /** `sent1(text)` — demo line 3193, the first sentence with a full stop. */
  readonly firstSentence: (text: string) => string;
  /** `fmtk(n)` — demo line 1856, `A$1.23m` / `A$456k`. */
  readonly fmtk: (aud: number) => string;
  /** `fmt$(n)` — demo line 1855, `A$1,234`. */
  readonly fmtMoney: (aud: number) => string;
}

/**
 * Everything the twenty-one steps read.
 *
 * Built by the caller and passed to {@link WorkflowStep.kpis} and
 * {@link WorkflowStep.blocks}. Nothing in this file constructs it, imports a
 * data module or touches a global; each step is a pure function of this value.
 *
 * Per-step usage is documented on the fields of {@link WorkflowDataPack} and
 * {@link WorkflowDerived}; the top-level datasets below are used as follows:
 *
 * - `fleet` / `machineById` — `reg`, `life`, `duty`, `rca`, `ready`, `crew`,
 *   `book`, `cost`, `entitle`, `plan`, `onsite`
 * - `classes` — `reg`, `life`
 * - `mineOrder` / `mines` — `reg`, `duty`, `plan`, `pipe`, `crew`, `cost`,
 *   `ready`, `entitle`, `onsite`
 * - `health` — `life`
 * - `workOrders` — `reg` (none), `intake`, `rca`, `plan`, `ready`, `cost`
 * - `shiftDays` — `duty`, `onsite`
 * - `alerts` — `assess`
 * - `fleetRules` — `rca`
 */
export interface WorkflowStepContext {
  /** `FLEET`. */
  readonly fleet: readonly FleetMachine[];
  /** `BYID`. */
  readonly machineById: Readonly<Record<string, FleetMachine | undefined>>;
  /** `CLASSES_`, keyed by model. */
  readonly classes: Readonly<Record<string, MachineClass>>;
  /** `MINE_ORDER` — mine codes in display order. */
  readonly mineOrder: readonly string[];
  /** `MINES`, keyed by mine code. */
  readonly mines: Readonly<Record<string, MineInfo>>;
  /**
   * `HEALTH`, **decorated with `band`**. The demo mutates every register row in
   * place at source line 1864 (`h.band = band(h.health_score).k`); this port
   * mutates nothing, so the caller decorates:
   *
   * ```ts
   * health: HEALTH.map((h) => ({ ...h, band: healthBand(h) }))
   * ```
   *
   * with `HEALTH` from `data/core.ts` and `healthBand` from `oem-engine.ts`.
   */
  readonly health: readonly ComponentHealth[];
  /** `WOS`. */
  readonly workOrders: readonly WorkOrder[];
  /** `SD` — the shift record. */
  readonly shiftDays: readonly ShiftDay[];
  /** `ALERTS`. */
  readonly alerts: readonly Alert[];
  /** `FLEET_RULES` — demo line 2821. */
  readonly fleetRules: readonly FleetRule[];
  /** The `B` data pack. */
  readonly pack: WorkflowDataPack;
  /** Pre-computed derivations. */
  readonly derived: WorkflowDerived;
  /** Pure lookup and formatting helpers. */
  readonly helpers: WorkflowHelpers;
}

/* ================================================================== *
 * Step records
 * ================================================================== */

/** Workflow step ids, in demo order. */
export const WORKFLOW_STEP_IDS = [
  'reg',
  'life',
  'duty',
  'forecast',
  'intake',
  'assess',
  'rca',
  'remote4',
  'entitle',
  'value',
  'plan',
  'ready',
  'pipe',
  'stock',
  'book',
  'crew',
  'skills',
  'pack',
  'onsite',
  'cost',
  'outcome',
] as const;

/** Closed set of workflow step ids. */
export type WorkflowStepId = (typeof WORKFLOW_STEP_IDS)[number];

/** A "Also in the platform" chip. */
export interface WorkflowAlso {
  /** Chip label, verbatim (HTML entities restored). */
  readonly label: string;
  readonly nav: SeedNav;
}

/** One workflow step. */
export interface WorkflowStep {
  readonly id: WorkflowStepId;
  /** The module this step belongs to. */
  readonly mod: ModuleId;
  /** Step name. */
  readonly name: string;
  /** One-line tag printed after the name. */
  readonly tag: string;
  /** The long-form lead paragraph. */
  readonly purpose: string;
  /** Hero KPI tiles. Pure function of the context. */
  readonly kpis: (ctx: WorkflowStepContext) => readonly WorkflowKpi[];
  /** The body. Pure function of the context. */
  readonly blocks: (ctx: WorkflowStepContext) => readonly WorkflowBlock[];
  /** Related-pages chips. */
  readonly also: readonly WorkflowAlso[];
}

/* ---- local helpers used by the step bodies ---- */

const kpi = (eyebrow: string, value: string | number, detail: string): WorkflowKpi => ({
  eyebrow,
  value: String(value),
  detail,
});

const also = (label: string, view: string, sub?: string): WorkflowAlso => ({
  label,
  nav: navShow(view, sub),
});

const mean = (values: readonly number[]): number =>
  values.reduce((a, b) => a + b, 0) / values.length;

const dash = <T,>(value: T | undefined | null, fallback = '—'): string =>
  value === undefined || value === null || value === '' ? fallback : String(value);

/** Demo lines 3396–3684 — `const WFS=[…]`, 21 steps in demo order. */
export const WORKFLOW_STEPS: readonly WorkflowStep[] = [
  /* ---------- 01 Asset Base ---------- */
  {
    id: 'reg',
    mod: 'installed',
    name: 'Installed base register',
    tag: 'one record per machine, and only one',
    purpose:
      'The foundation every other step reads from: customer → site → fleet → machine → component, with serial numbers, configuration, meters, warranty and service contract on the same record. The customer’s own system stays the system of record on their side and is cross-referenced, never overwritten.',
    kpis: (ctx) => [
      kpi('Machines', ctx.fleet.length, 'in the register'),
      kpi('Models', Object.keys(ctx.classes).length, 'across six classes'),
      kpi('Maximo records', Object.keys(ctx.pack.mx).length, 'service register'),
      kpi('Under warranty', ctx.derived.warrantyActive.length, 'factory cover still running'),
    ],
    blocks: (ctx) => {
      const rows = ctx.fleet
        .slice()
        .sort((a, b) => ctx.mineOrder.indexOf(a.mine) - ctx.mineOrder.indexOf(b.mine))
        .slice(0, 14)
        .map((f): readonly WorkflowCell[] => {
          const m = ctx.helpers.mxOf(f.machine_id) ?? {};
          const w = m.warranty ?? {};
          const c = m.servicecontract ?? {};
          return [
            mcell(f.machine_id, true),
            f.model,
            ctx.mines[f.mine].name,
            dash(m.installdate),
            f.engine_hours.toLocaleString(),
            dash(m.serialnum),
            dash(c.contractnum),
            {
              kind: 'pill',
              text: dash(w.status),
              tone: w.status === 'Active' ? 'good' : 'mute',
            },
          ];
        });
      const byClass = Object.entries(ctx.classes)
        .map(([k, v]): readonly WorkflowCell[] | null => {
          const fs = ctx.fleet.filter((f) => f.model === k);
          if (!fs.length) return null;
          const h = fs.map((f) => f.engine_hours);
          const sc = fs.map((f) =>
            Math.min(...ctx.helpers.healthOf(f.machine_id).map((x) => x.health_score)),
          );
          return [
            k,
            v.name ?? '',
            v.subclass ?? '',
            String(fs.length),
            Math.round(mean(h)).toLocaleString(),
            `${Math.round(mean(sc))}/100`,
            dash(v.drive),
          ];
        })
        .filter((r): r is readonly WorkflowCell[] => r !== null);
      const ct = ctx.pack.mx_contracts.map(
        (c): readonly WorkflowCell[] => [
          c.contractnum,
          c.customer,
          c.type,
          String(c.machines),
          `A$${c.labour_rate_aud_h}/h`,
          daysWord(ctx.helpers.daysTo(c.end)),
          dash(c.contractor),
        ],
      );
      return [
        wfCard(
          'One record per machine',
          'the first fourteen rows of the register — every field comes off the Maximo asset record',
          [
            wfTable(
              ['Machine', 'Model', 'Site', 'Installed', '#Engine h', 'Serial', 'Contract', 'Warranty'],
              rows,
            ),
            wfNote([
              nt(`${ctx.fleet.length} machines in the register · `),
              nlink('open the full population list', navShow('population')),
              nt(' · '),
              nlink('see a full Maximo record', navShow('machine', 'L-18', 'record')),
            ]),
          ],
        ),
        wfCard(
          'What the population is made of',
          'by model, with the configuration attributes that drive the service plan',
          [
            wfTable(
              ['Model', 'Description', 'Class', '#Units', '#Mean engine h', '#Mean condition', 'Drive'],
              byClass,
            ),
          ],
        ),
        wfCard(
          'Service contracts on the base',
          'one per site — the contract decides the rate, the interval and who pays',
          [
            wfTable(
              ['Contract', 'Customer', 'Type', '#Machines', '#Labour rate', 'Ends in', 'Contractor'],
              ct,
            ),
          ],
        ),
      ];
    },
    also: [
      also('Population list', 'population'),
      also('Sites map', 'overview'),
      also('Connectors', 'connectors'),
    ],
  },

  {
    id: 'life',
    mod: 'installed',
    name: 'Machine life & meters',
    tag: 'hours, age and what has been fitted to each unit',
    purpose:
      'The life-to-date view of the base: meters and duty by unit, which machines are carrying the hours, and the component population underneath them — how many of each part are in service, how old they are and how many are past their change interval.',
    kpis: (ctx) => [
      kpi(
        'Engine hours',
        `${Math.round(ctx.fleet.reduce((a, f) => a + f.engine_hours, 0) / 1000)}k`,
        'life-to-date across the base',
      ),
      kpi(
        'Mean hours',
        Math.round(mean(ctx.fleet.map((f) => f.engine_hours))).toLocaleString(),
        'per machine',
      ),
      kpi('Components tracked', ctx.health.length, 'with a condition score'),
      kpi('Past interval', ctx.derived.pastInterval.length, 'components over their change hours'),
    ],
    blocks: (ctx) => {
      const top = ctx.fleet
        .slice()
        .sort((a, b) => b.engine_hours - a.engine_hours)
        .slice(0, 10)
        .map((f): readonly WorkflowCell[] => {
          const c = ctx.pack.cost_per_machine.find((x) => x.machine_id === f.machine_id);
          const worst = ctx.helpers
            .healthOf(f.machine_id)
            .slice()
            .sort((a, b) => a.health_score - b.health_score)[0];
          return [
            mcell(f.machine_id, true),
            f.model,
            ctx.mines[f.mine].name,
            f.engine_hours.toLocaleString(),
            c?.cost_per_op_hour_aud ? `A$${c.cost_per_op_hour_aud}` : '—',
            dash(worst?.component),
            `${worst?.health_score ?? '—'}/100`,
          ];
        });
      const cls = new Map<string, { n: number; h: number }>();
      for (const f of ctx.fleet) {
        const e = cls.get(f.subclass) ?? { n: 0, h: 0 };
        e.n += 1;
        e.h += f.engine_hours;
        cls.set(f.subclass, e);
      }
      const bars: readonly WorkflowBar[] = [...cls].map(([k, v]) => ({
        label: k,
        value: Math.round(v.h / v.n),
        text: `${Math.round(v.h / v.n).toLocaleString()} h mean · ${v.n} units`,
      }));
      const comp = new Map<string, { n: number; s: number; over: number }>();
      for (const h of ctx.health) {
        const e = comp.get(h.component) ?? { n: 0, s: 0, over: 0 };
        e.n += 1;
        e.s += h.health_score;
        if (h.hours_since_change > ctx.helpers.intervalOf(h.component)) e.over += 1;
        comp.set(h.component, e);
      }
      const crows = [...comp]
        .sort((a, b) => b[1].over - a[1].over || b[1].n - a[1].n)
        .slice(0, 12)
        .map(([k, v]): readonly WorkflowCell[] => [
          k,
          String(v.n),
          `${Math.round(v.s / v.n)}/100`,
          `${ctx.helpers.intervalOf(k).toLocaleString()} h`,
          v.over ? { kind: 'emphasis', text: String(v.over), tone: 'warn' } : '0',
        ]);
      return [
        wfCard(
          'The units carrying the hours',
          'ten highest-hour machines, with what each one costs to run and its weakest component',
          [
            wfTable(
              ['Machine', 'Model', 'Site', '#Engine h', '#Cost/op h', 'Weakest component', '#Score'],
              top,
            ),
          ],
        ),
        wfCard('Mean hours by class', 'how hard each class has worked', [wfBars(bars)]),
        wfCard(
          'Component population',
          'every serialised component in service, and how many are past their change interval',
          [
            wfTable(
              ['Component', '#In service', '#Mean condition', '#Change interval', '#Past interval'],
              crows,
            ),
            wfNote([
              nt('Condition charts for each machine sit in '),
              nlink('Fleet health', navShow('fleet')),
              nt('; the exchange pool is in the '),
              nlink('component lifecycle loop', navShow('loops', 'component')),
              nt('.'),
            ]),
          ],
        ),
      ];
    },
    also: [also('Fleet health', 'fleet'), also('Component lifecycle loop', 'loops', 'component')],
  },

  /* ---------- 02 Customer & Asset Needs ---------- */
  {
    id: 'duty',
    mod: 'demand',
    name: 'Duty & demand signals',
    tag: 'where the next job comes from, before anyone raises it',
    purpose:
      'Duty is the leading indicator. This step reads how hard each site and class is being worked, what the machines are reporting while they work, and which of those signals are strong enough to become a case.',
    kpis: (ctx) => [
      kpi('Shift records', ctx.shiftDays.length.toLocaleString(), 'machine-days on file'),
      kpi(
        'Alarm events',
        ctx.pack.events_daily.rows.reduce((a, r) => a + r[4], 0).toLocaleString(),
        'last fortnight',
      ),
      kpi('Critical events', ctx.pack.events_critical.length, 'escalated by severity'),
      kpi(
        'Watch conditions',
        ctx.fleet.filter((f) => ctx.helpers.statusOf(f.machine_id) === 'poor').length,
        'machines drifting down a band',
      ),
    ],
    blocks: (ctx) => {
      const rows = ctx.mineOrder.map((m): readonly WorkflowCell[] => {
        const ids = ctx.fleet.filter((f) => f.mine === m).map((f) => f.machine_id);
        const sd = ctx.shiftDays.filter((s) => ids.includes(s.machine_id));
        const h = sd.reduce((a, s) => a + s.hours, 0) / sd.length;
        const al = sd.reduce((a, s) => a + s.alarms, 0) / sd.length;
        const dl = sd.reduce((a, s) => a + (s.delays ? s.delays.split(';').length : 0), 0) / sd.length;
        return [
          ctx.mines[m].name,
          String(ids.length),
          h.toFixed(1),
          al.toFixed(1),
          dl.toFixed(1),
          `${Math.round(sd.reduce((a, s) => a + s.diesel_l, 0) / sd.length)} L`,
          {
            kind: 'pill',
            text: ctx.helpers.dataModeLabel(ctx.mines[m]),
            tone: ctx.mines[m].data_mode === 'Live' ? 'good' : 'mute',
          },
        ];
      });
      const codes = new Map<string, { n: number; sev: string; m: Set<string> }>();
      for (const r of ctx.pack.events_daily.rows) {
        const e = codes.get(r[2]) ?? { n: 0, sev: r[3], m: new Set<string>() };
        e.n += r[4];
        e.m.add(r[0]);
        codes.set(r[2], e);
      }
      const crows = [...codes]
        .sort((a, b) => b[1].n - a[1].n)
        .slice(0, 10)
        .map(([k, v]): readonly WorkflowCell[] => [
          mono(k),
          v.sev,
          String(v.n),
          String(v.m.size),
          { kind: 'machines', ids: [...v.m].slice(0, 4) },
        ]);
      return [
        wfCard(
          'Duty profile by site',
          'daily means over the shift record — the workload the service plan has to keep up with',
          [
            wfTable(
              ['Site', '#Machines', '#Shift h/day', '#Alarms/day', '#Delays/day', '#Diesel/day', 'Data'],
              rows,
            ),
          ],
        ),
        wfCard(
          'What the machines are reporting',
          'alarm codes by volume across the fortnight, and which units raised them',
          [wfTable(['Code', 'Severity', '#Events', '#Machines', 'Raised by'], crows)],
        ),
        wfCard('What a signal becomes', 'the four routes out of this step', [
          {
            kind: 'routes',
            routes: [
              {
                title: 'Condition drift',
                text: 'A component crossing a band opens a case in 03 and an assessment in 04.',
                link: { label: '04 Assess & Prescribe', nav: navShow('module', 'diagnose') },
              },
              {
                title: 'Duty above plan',
                text: 'Hours ahead of the interval pull the next preventive service forward.',
                link: { label: 'Predicted parts demand', nav: navShow('wf', 'forecast') },
              },
              {
                title: 'Customer request',
                text: 'An email, a call or a portal request becomes a case with the same fields.',
                link: { label: 'Case intake & triage', nav: navShow('wf', 'intake') },
              },
              {
                title: 'Morning report',
                text: 'The hotspots are summarised at 06:00 for the whole network.',
                link: { label: 'S.O.S report', nav: navShow('sos') },
              },
            ],
          },
        ]),
      ];
    },
    also: [also('S.O.S report', 'sos'), also('Live issues', 'issues')],
  },

  {
    id: 'forecast',
    mod: 'demand',
    name: 'Predicted parts demand',
    tag: 'what the next thirty days will consume',
    purpose:
      'Turning the signals into a number: which parts the predicted failures and the due services will consume, whether the site store or the distribution centre can cover it, and what has to be ordered now to land inside the window.',
    kpis: (ctx) => [
      kpi('Predicted lines', ctx.derived.predictedUsage.length, 'part / site combinations'),
      kpi(
        'Gaps',
        ctx.derived.predictedUsage.filter((r) => r.gap > 0).length,
        'not covered by the site store',
      ),
      kpi(
        'Services due',
        ctx.pack.mx_pm.filter((p) => ctx.helpers.daysTo(p.nextdue_date) <= 30).length,
        'in the next 30 days',
      ),
      kpi(
        'Below minimum',
        ctx.pack.inventory.filter(
          (i) => i.qty_on_hand < i.min_level && i.location.includes('site'),
        ).length,
        'site store lines',
      ),
    ],
    blocks: (ctx) => {
      const pu = ctx.derived.predictedUsage
        .slice(0, 12)
        .map((r): readonly WorkflowCell[] => [
          mono(r.part_no),
          r.desc ?? '',
          ctx.mines[r.mine].name,
          String(Math.ceil(r.demand)),
          String(r.store ?? 0),
          r.dcq === undefined || r.dcq === null ? '—' : String(r.dcq),
          r.gap > 0 ? { kind: 'emphasis', text: String(r.gap), tone: 'warn' } : '0',
          r.prop ?? '',
        ]);
      const pm = ctx.pack.mx_pm
        .filter((p) => ctx.helpers.daysTo(p.nextdue_date) <= 30)
        .sort((a, b) => ctx.helpers.daysTo(a.nextdue_date) - ctx.helpers.daysTo(b.nextdue_date))
        .slice(0, 12)
        .map((p): readonly WorkflowCell[] => [
          mcell(p.assetnum),
          mono(p.pmnum),
          p.description,
          p.nextdue_date,
          daysWord(ctx.helpers.daysTo(p.nextdue_date)),
          `${p.leadtime_days} d`,
          `${p.frequency} ${p.frequnit.toLowerCase()}`,
        ]);
      return [
        wfCard(
          'Predicted usage, next thirty days',
          'demand weighted by condition band, checked against the site store and the distribution centre',
          [
            wfTable(
              ['Part', 'Description', 'Site', '#Demand', '#On site', '#At DC', '#Gap', 'Proposal'],
              pu,
            ),
            wfNote([
              nt('Requisitions raised from this table land in '),
              nlink('Pipeline & expediting', navShow('wf', 'pipe')),
              nt(' as drafts.'),
            ]),
          ],
        ),
        wfCard(
          'Preventive services falling due',
          'from the Maximo PM schedule — the lead time is how early the parts have to be committed',
          [wfTable(['Machine', 'PM', 'Service', 'Due', 'In', 'Lead time', 'Interval'], pm)],
        ),
      ];
    },
    also: [
      also('Parts pipeline', 'partsflow'),
      also('Predictive usage detail', 'partsflow', 'predict'),
    ],
  },

  /* ---------- 03 Case Management ---------- */
  {
    id: 'intake',
    mod: 'orchestrate',
    name: 'Case intake & triage',
    tag: 'every signal becomes one case, with one owner',
    purpose:
      'The front door. Whatever the channel — a live alert, an RMS exception, a customer email, a portal case, a technician note — it lands as one case with the machine, the site, the priority, the owner and a clock. Nothing is worked on that has not been through here.',
    kpis: (ctx) => [
      kpi('Open cases', ctx.derived.jobs.length, 'across the ten modules'),
      kpi('Channels', 6, 'feeding the queue'),
      kpi('Critical', ctx.derived.jobs.filter((j) => j.kind === 'Alert').length, 'raised as alerts'),
      kpi(
        'Waiting on a person',
        ctx.workOrders.filter((w) => w.status.startsWith('Draft')).length,
        'agent drafts',
      ),
    ],
    blocks: (ctx) => {
      const J = ctx.derived.jobs;
      const kinds = new Map<string, CaseJob[]>();
      for (const j of J) {
        const list = kinds.get(j.kind) ?? [];
        list.push(j);
        kinds.set(j.kind, list);
      }
      const ch = [...kinds]
        .sort((a, b) => b[1].length - a[1].length)
        .map(([k, v]): readonly WorkflowCell[] => [
          k,
          String(v.length),
          v[0].title.slice(0, 58),
          v[0].machine ? mcell(v[0].machine) : '—',
          v[0].owner,
        ]);
      const q = J.slice(0, 14).map((j): readonly WorkflowCell[] => [
        mono(j.id),
        j.kind,
        j.machine ? mcell(j.machine) : '—',
        j.mine ? ctx.mines[j.mine].name : '—',
        j.title.slice(0, 52),
        MODULE_BY_ID[j.stage as ModuleId]?.short ?? j.stage,
        j.owner,
      ]);
      return [
        wfCard(
          'Where the work comes in',
          'one row per channel, with the oldest example still open on it',
          [wfTable(['Channel', '#Open', 'Example', 'Machine', 'Owner'], ch)],
        ),
        wfCard(
          'Triage queue',
          'the case, the machine, the module it has been routed to and who holds it',
          [
            wfTable(['Case', 'Type', 'Machine', 'Site', 'What', 'Routed to', 'Owner'], q),
            wfNote([
              nt(`${J.length} cases open · `),
              nlink('see them as a board', navShow('jobboard')),
              nt(' · '),
              nlink('the ones that are hurting now', navShow('issues')),
            ]),
          ],
        ),
        wfCard('Routing rules', 'how the orchestrator decides where a case goes', [
          {
            kind: 'routes',
            routes: [
              {
                title: 'Safety or restriction',
                text: 'Straight to 04 with the safety agent attached; a restriction is drafted before anything else.',
              },
              {
                title: 'Predicted failure inside its window',
                text: '04 assesses, then 06 plans it into the next window — never a breakdown call-out.',
              },
              {
                title: 'Parts-only request',
                text: 'Skips assessment and goes to 07 against the contract entitlement checked in 05.',
              },
              {
                title: 'Commercial question',
                text: 'Routed to 05 with the contract and warranty record already attached.',
              },
            ],
          },
        ]),
      ];
    },
    also: [also('Job board', 'jobboard'), also('Case-to-closure trace', 'jobrun')],
  },

  /* ---------- 04 Assess & Prescribe ---------- */
  {
    id: 'assess',
    mod: 'diagnose',
    name: 'Diagnosis & prescription',
    tag: 'from a signal to a finding to a prescribed action',
    purpose:
      'The assessment itself: what the signal was, what the agents matched it against, how confident the model is, how long the window is, and exactly what is prescribed. Nothing here touches a machine — every prescription is a draft until a person approves it.',
    kpis: (ctx) => [
      kpi('Open assessments', ctx.alerts.length, 'with a prescription drafted'),
      kpi(
        'Mean confidence',
        `${Math.round(mean(ctx.alerts.map((x) => x.conf)) * 100)}%`,
        'across the open set',
      ),
      kpi(
        'Evidence sources',
        new Set(ctx.alerts.flatMap((a) => a.evidence.map((e) => e.source))).size,
        'systems pulled in',
      ),
      kpi(
        'Critical',
        ctx.alerts.filter((a) => a.sev === 'crit').length,
        'act now, not at the next service',
      ),
    ],
    blocks: (ctx) => {
      const rows = ctx.alerts.map((a): readonly WorkflowCell[] => {
        const f = ctx.machineById[a.machine];
        const step =
          a.chain.find((c) => /work order|safety/i.test(c.agent)) ?? a.chain[a.chain.length - 1];
        return [
          mono(a.id),
          mcell(a.machine, true),
          f ? ctx.mines[f.mine].name : '—',
          a.comp,
          a.title.slice(0, 46),
          `${Math.round(a.conf * 100)}%`,
          a.window,
          stripTags(step ? step.output : '').slice(0, 62),
        ];
      });
      const ev = new Map<string, { n: number; ex: string }>();
      for (const a of ctx.alerts) {
        for (const e of a.evidence) {
          const cur = ev.get(e.source) ?? { n: 0, ex: e.detail };
          cur.n += 1;
          ev.set(e.source, cur);
        }
      }
      const erows = [...ev]
        .sort((a, b) => b[1].n - a[1].n)
        .map(([k, v]): readonly WorkflowCell[] => [k, String(v.n), v.ex.slice(0, 64)]);
      const conf: readonly WorkflowBar[] = ctx.alerts
        .slice()
        .sort((a, b) => b.conf - a.conf)
        .map((a) => ({
          label: `${a.machine} · ${a.comp}`,
          value: Math.round(a.conf * 100),
          text: `${Math.round(a.conf * 100)}%`,
          colour: a.sev === 'crit' ? 'var(--vpoor)' : 'var(--poor)',
        }));
      return [
        wfCard(
          'Signal → finding → prescription',
          'every open assessment on one line; the full chain and the evidence sit behind each one',
          [
            wfTable(
              ['Ref', 'Machine', 'Site', 'Component', 'Finding', '#Confidence', 'Window', 'Prescribed'],
              rows,
            ),
            wfNote([
              nlink('Open the alert pages', navShow('alerts')),
              nt(' for the evidence, the chart and the agent chain on each.'),
            ]),
          ],
        ),
        wfCard(
          'Evidence the agents pulled',
          'which systems the finding was built from, and how often each was needed',
          [wfTable(['Source', '#Used', 'Example'], erows)],
        ),
        wfCard(
          'Model confidence',
          'how sure the diagnostic agent is on each — anything under 80% goes to a person before a prescription is drafted',
          [wfBars(conf)],
        ),
      ];
    },
    also: [also('Alerts', 'alerts'), also('Remote diagnosis', 'wf', 'remote4')],
  },

  {
    id: 'rca',
    mod: 'diagnose',
    name: 'Root cause analysis',
    tag: 'why it failed, and what the fleet learns from it',
    purpose:
      'The backward-looking half of the same module. Closed failures are analysed by mode and component, repeats are found across the fleet rather than the machine, and what comes out is a rule, a bulletin or a campaign — not a one-off repair note.',
    kpis: (ctx) => [
      kpi(
        'Corrective orders',
        ctx.workOrders.filter((w) => w.wo_type !== 'Preventive').length.toLocaleString(),
        '18 months of history',
      ),
      kpi(
        'Failure modes',
        new Set(ctx.workOrders.filter((w) => w.wo_type !== 'Preventive').map((w) => w.component))
          .size,
        'distinct components',
      ),
      kpi('Fleet rules live', ctx.fleetRules.length, 'each came out of one job'),
      kpi('Bulletins', ctx.pack.bulletins.length, 'in force'),
    ],
    blocks: (ctx) => {
      const cw = ctx.workOrders.filter((w) => w.wo_type !== 'Preventive');
      const by = new Map<string, { n: number; cost: number; m: Set<string> }>();
      for (const w of cw) {
        const k = w.component || '—';
        const e = by.get(k) ?? { n: 0, cost: 0, m: new Set<string>() };
        e.n += 1;
        e.cost += w.total_direct_cost_aud || 0;
        e.m.add(w.machine_id);
        by.set(k, e);
      }
      const ranked = [...by].sort((a, b) => b[1].n - a[1].n);
      const rows = ranked.slice(0, 12).map(([k, v]): readonly WorkflowCell[] => [
        k,
        String(v.n),
        String(v.m.size),
        `A$${Math.round(v.cost / v.n).toLocaleString()}`,
        `A$${Math.round(v.cost / 1000)}k`,
      ]);
      const bars: readonly WorkflowBar[] = ranked.slice(0, 8).map(([k, v]) => ({
        label: k,
        value: v.n,
        text: `${v.n} failures`,
      }));
      const rep = new Map<string, number>();
      for (const w of cw) {
        const k = `${w.machine_id}|${w.component ?? ''}`;
        rep.set(k, (rep.get(k) ?? 0) + 1);
      }
      const rrows = [...rep]
        .filter(([, n]) => n >= 3)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([k, n]): readonly WorkflowCell[] => {
          const [id, c] = k.split('|');
          const f = ctx.machineById[id];
          return [
            mcell(id, true),
            f ? f.model : '—',
            f ? ctx.mines[f.mine].name : '—',
            c,
            String(n),
            { kind: 'dim', text: 'same component, 18 months' },
          ];
        });
      const rules = ctx.fleetRules.map((r): readonly WorkflowCell[] => [r[0], mono(r[1]), r[2]]);
      return [
        wfCard(
          'Failure modes across the fleet',
          'ranked by how often the component has been the reason for a corrective order',
          [
            wfTable(['Component', '#Failures', '#Machines', '#Mean cost', '#Total'], rows),
            wfBars(bars),
          ],
        ),
        wfCard(
          'Repeat failures',
          'the same component going back on the same machine — a practice problem, not a parts problem',
          [wfTable(['Machine', 'Model', 'Site', 'Component', '#Times', 'Window'], rrows)],
        ),
        wfCard('What came out of it', 'rules now running across every machine of that type', [
          wfTable(['Rule', 'From', 'Applies to'], rules),
          wfNote([
            nt('The incidents these came from are replayed in '),
            nlink('Incidents', navShow('incident')),
            nt('; the loop itself is '),
            nlink('fleet learning', navShow('loops', 'learning')),
            nt('.'),
          ]),
        ]),
      ];
    },
    also: [also('Incidents', 'incident'), also('Fleet learning loop', 'loops', 'learning')],
  },

  {
    id: 'remote4',
    mod: 'diagnose',
    name: 'Remote diagnosis',
    tag: 'the expert on the machine without the flight',
    purpose:
      'When the assessment needs eyes on the machine, a remote session is opened instead of a call-out. This step tracks what was diagnosed remotely, how long it took, what it saved, and which sessions still ended in a visit.',
    kpis: (ctx) => [
      kpi('Sessions', ctx.pack.xreach_sessions.length, 'last ten weeks'),
      kpi(
        'Mean duration',
        `${Math.round(mean(ctx.pack.xreach_sessions.map((s) => s.duration_min)))} min`,
        'per session',
      ),
      kpi('Sites covered', new Set(ctx.pack.xreach_sessions.map((s) => s.site)).size, 'of six'),
      kpi('Experts', new Set(ctx.pack.xreach_sessions.map((s) => s.expert)).size, 'on the network'),
    ],
    blocks: (ctx) => {
      const rows = ctx.pack.xreach_sessions
        .slice()
        .sort((a, b) => b.started.localeCompare(a.started))
        .slice(0, 12)
        .map((s): readonly WorkflowCell[] => [
          s.started.replace('T', ' '),
          s.site,
          s.machine ? mcell(s.machine) : '—',
          s.topic,
          dash(s.component),
          s.expert,
          `${s.duration_min} min`,
          dash(s.device),
        ]);
      const top = new Map<string, number>();
      for (const s of ctx.pack.xreach_sessions) top.set(s.topic, (top.get(s.topic) ?? 0) + 1);
      const trows: readonly WorkflowBar[] = [...top]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 8)
        .map(([k, v]) => ({ label: k, value: v, text: `${v} session${v === 1 ? '' : 's'}` }));
      const dev = new Map<string, number>();
      for (const s of ctx.pack.xreach_sessions) {
        const k = `${s.device ?? '—'} · ${s.network ?? '—'}`;
        dev.set(k, (dev.get(k) ?? 0) + 1);
      }
      const drows = [...dev]
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]): readonly WorkflowCell[] => [
          k,
          String(v),
          `${((v / ctx.pack.xreach_sessions.length) * 100).toFixed(0)}%`,
        ]);
      return [
        wfCard(
          'Sessions on the machine',
          'the twelve most recent — field worker, expert, what was looked at and for how long',
          [
            wfTable(
              ['Started', 'Site', 'Machine', 'Topic', 'Component', 'Expert', '#Duration', 'Device'],
              rows,
            ),
          ],
        ),
        wfCard('What gets escalated', 'the topics that most often need an expert', [wfBars(trows)]),
        wfCard(
          'How they connect',
          'device and network on the session — the constraint underground is bandwidth, not willingness',
          [
            wfTable(['Device · network', '#Sessions', '#Share'], drows),
            wfNote([
              nt('The expert roster and who is on cover sits in '),
              nlink('Skills & certification', navShow('wf', 'skills')),
              nt('; the session pages are in '),
              nlink('Remote support', navShow('remote')),
              nt('.'),
            ]),
          ],
        ),
      ];
    },
    also: [also('Remote support', 'remote'), also('Skills & certification', 'wf', 'skills')],
  },

  /* ---------- 05 Commercial & Entitlement ---------- */
  {
    id: 'entitle',
    mod: 'commercial',
    name: 'Entitlement & warranty',
    tag: 'who pays, before the work is committed',
    purpose:
      'Every job gets an entitlement decision before it is planned: is it contract, warranty, goodwill or chargeable, and within what limit. Getting it wrong here is what turns a service into an argument three weeks later.',
    kpis: (ctx) => [
      kpi('Contracts', ctx.pack.mx_contracts.length, '100% of the base covered'),
      kpi('Under warranty', ctx.derived.warrantyActive.length, 'machines with factory cover'),
      kpi(
        'Warranty cases',
        ctx.pack.cases.filter((c) => /Warranty/.test(c.type)).length,
        'open or decided',
      ),
      kpi(
        'Chargeable',
        ctx.derived.jobs.filter((j) => {
          const w = ctx.helpers.mxOf(j.machine ?? '')?.warranty;
          return !w || w.status !== 'Active';
        }).length,
        'jobs outside warranty',
      ),
    ],
    blocks: (ctx) => {
      const J = ctx.derived.jobs
        .filter((j): j is CaseJob & { machine: string } => Boolean(j.machine))
        .slice(0, 12)
        .map((j): readonly WorkflowCell[] => {
          const m = ctx.helpers.mxOf(j.machine) ?? {};
          const w = m.warranty ?? {};
          const c = m.servicecontract ?? {};
          const active = w.status === 'Active';
          return [
            mono(j.id),
            mcell(j.machine),
            j.mine ? ctx.mines[j.mine].name : '—',
            dash(c.contractnum),
            dash(c.coverage ?? c.type),
            { kind: 'pill', text: dash(w.status), tone: active ? 'good' : 'mute' },
            active ? 'Warranty' : /PM|Preventive/i.test(j.kind) ? 'Contract' : 'Chargeable',
          ];
        });
      const wc = ctx.pack.cases
        .filter((c) => /Warranty/.test(c.type))
        .map((c): readonly WorkflowCell[] => [
          mono(c.case_no),
          c.opened,
          mcell(c.machine_id),
          ctx.mines[c.mine].name,
          c.subject.slice(0, 60),
          c.status.slice(0, 52),
          c.owner,
        ]);
      const cov = ctx.pack.mx_contracts.map((c): readonly WorkflowCell[] => {
        const ids = ctx.fleet
          .filter((f) => ctx.mines[f.mine].name === c.customer)
          .map((f) => f.machine_id);
        const w = ids.filter((id) => ctx.helpers.mxOf(id)?.warranty?.status === 'Active').length;
        return [
          c.customer,
          c.contractnum,
          String(c.machines),
          String(w),
          `A$${c.labour_rate_aud_h}/h`,
          c.end,
          daysWord(ctx.helpers.daysTo(c.end)),
        ];
      });
      return [
        wfCard(
          'Entitlement on today’s open jobs',
          'the decision the commercial agent has drafted against each one',
          [
            wfTable(
              ['Case', 'Machine', 'Site', 'Contract', 'Coverage', 'Warranty', 'Who pays'],
              J,
            ),
          ],
        ),
        wfCard('Warranty cases', 'with the evidence each decision was made on', [
          wfTable(['Case', 'Opened', 'Machine', 'Site', 'Subject', 'Decision', 'Owner'], wc),
        ]),
        wfCard(
          'Coverage by customer',
          'contract, machines covered, how many still carry factory warranty and when the contract ends',
          [
            wfTable(
              ['Customer', 'Contract', '#Machines', '#Under warranty', '#Rate', 'Ends', 'In'],
              cov,
            ),
          ],
        ),
      ];
    },
    also: [also('Customers', 'customers'), also('Warranty & campaigns loop', 'loops', 'warranty')],
  },

  {
    id: 'value',
    mod: 'commercial',
    name: 'Quote, margin & growth',
    tag: 'what the work is worth, and what it opens up',
    purpose:
      'The commercial read on the same base: where the aftermarket revenue comes from, how much of it is reactive (the expensive kind), which contracts are coming up for renewal and which machines are candidates for an upgrade or a broader agreement.',
    kpis: (ctx) => {
      const r = ctx.derived.revenue;
      return [
        kpi('Parts revenue', ctx.helpers.fmtk(r.parts12), 'last 12 months'),
        kpi('Technician labour', ctx.helpers.fmtk(r.labour10w), 'last 10 weeks'),
        kpi('Reactive share', `${Math.round(r.reactiveShare * 100)}%`, 'of parts spend'),
        kpi(
          'Upgrade candidates',
          ctx.derived.upgradeCandidates.length,
          'machines on a call-off contract',
        ),
      ];
    },
    blocks: (ctx) => {
      const r = ctx.derived.revenue;
      const bars: readonly WorkflowBar[] = [
        { label: 'Parts', value: r.parts12, text: ctx.helpers.fmtk(r.parts12) },
        { label: 'Technician labour', value: r.labour10w, text: ctx.helpers.fmtk(r.labour10w) },
        { label: 'Breakdown freight', value: r.freightBd, text: ctx.helpers.fmtk(r.freightBd) },
      ];
      const rn = ctx.derived.renewals.map((c): readonly WorkflowCell[] => [
        c.customer,
        c.contractnum,
        c.type,
        String(c.machines),
        c.end,
        daysWord(c.days),
        String(
          ctx.derived.upgradeCandidates.filter((f) => ctx.mines[f.mine].name === c.customer).length,
        ),
      ]);
      const uc = ctx.derived.upgradeCandidates.slice(0, 10).map((f): readonly WorkflowCell[] => {
        const c = ctx.helpers.mxOf(f.machine_id)?.servicecontract ?? {};
        return [
          mcell(f.machine_id, true),
          f.model,
          ctx.mines[f.mine].name,
          f.engine_hours.toLocaleString(),
          dash(c.type),
          'call-off today — a full-coverage agreement prices the risk instead',
        ];
      });
      return [
        wfCard(
          'Where the aftermarket revenue comes from',
          'the three lines the contract is priced against',
          [
            wfBars(bars),
            wfNote([
              nt(
                `${Math.round(r.reactiveShare * 100)}% of parts spend is reactive. Every point moved from reactive to planned is margin, not just cost.`,
              ),
            ]),
          ],
        ),
        wfCard('Renewals', 'ordered by how soon each contract ends', [
          wfTable(
            ['Customer', 'Contract', 'Type', '#Machines', 'Ends', 'In', '#Upgrade candidates'],
            rn,
          ),
        ]),
        wfCard(
          'Upgrade candidates',
          'machines running on call-off where the failure history already justifies full coverage',
          [wfTable(['Machine', 'Model', 'Site', '#Engine h', 'Contract today', 'Why'], uc)],
        ),
      ];
    },
    also: [also('Cost summary', 'costs'), also('Commercial opportunity loop', 'loops', 'commercial')],
  },

  /* ---------- 06 Service Planning ---------- */
  {
    id: 'plan',
    mod: 'promise',
    name: 'Service plan & window',
    tag: 'the work placed in a window the mine can actually give up',
    purpose:
      'Planning is not a date, it is a window. This step lines the due work up against the shift plan, the ground conditions and the site access, so the machine is offered when the mine can release it and the parts and people can be there.',
    kpis: (ctx) => [
      kpi(
        'Due in 30 days',
        ctx.pack.mx_pm.filter((p) => ctx.helpers.daysTo(p.nextdue_date) <= 30).length,
        'preventive services',
      ),
      kpi(
        'Scheduled orders',
        ctx.workOrders.filter((w) => w.status === 'Scheduled').length,
        'already in a window',
      ),
      kpi(
        'Overdue',
        ctx.pack.mx_pm.filter((p) => ctx.helpers.daysTo(p.nextdue_date) < 0).length,
        'past the interval',
      ),
      kpi('Sites', ctx.mineOrder.length, 'each with its own access rules'),
    ],
    blocks: (ctx) => {
      const pm = ctx.pack.mx_pm
        .filter((p) => ctx.helpers.daysTo(p.nextdue_date) <= 45)
        .sort((a, b) => ctx.helpers.daysTo(a.nextdue_date) - ctx.helpers.daysTo(b.nextdue_date))
        .slice(0, 14)
        .map((p): readonly WorkflowCell[] => {
          const f = ctx.machineById[p.assetnum];
          const d = ctx.helpers.daysTo(p.nextdue_date);
          return [
            mcell(p.assetnum),
            f ? f.model : '—',
            f ? ctx.mines[f.mine].name : '—',
            p.description.slice(0, 44),
            p.nextdue_date,
            {
              kind: 'emphasis',
              text: daysWord(d),
              ...(d < 0 ? { tone: 'vpoor' as const } : d < 7 ? { tone: 'warn' as const } : {}),
            },
            `${p.leadtime_days} d`,
            p.jobplan,
          ];
        });
      const split = ctx.mineOrder.map((m): readonly WorkflowCell[] => {
        const w = ctx.workOrders.filter((x) => x.mine === m);
        const pmn = w.filter((x) => x.wo_type === 'Preventive').length;
        const cor = w.length - pmn;
        const pct = Math.round((pmn / w.length) * 100);
        return [
          ctx.mines[m].name,
          String(w.length),
          String(pmn),
          String(cor),
          `${pct}%`,
          { kind: 'meter', pct },
        ];
      });
      const si = ctx.mineOrder.map((m): readonly WorkflowCell[] => {
        const s = ctx.pack.siteinfo[m];
        return [
          ctx.mines[m].name,
          s ? s.access_and_logistics.roster : '—',
          s ? ctx.helpers.firstSentence(s.mine_services.ventilation) : '—',
          (s ? s.ground_conditions.seismic : '—').slice(0, 58),
        ];
      });
      return [
        wfCard(
          'What is due, and when the window is',
          'preventive services inside the next 45 days, with the lead time the parts need',
          [
            wfTable(
              ['Machine', 'Model', 'Site', 'Service', 'Due', 'In', 'Lead time', 'Job plan'],
              pm,
            ),
          ],
        ),
        wfCard(
          'Planned against unplanned',
          'the ratio the whole operating model is trying to move',
          [
            wfTable(
              ['Site', '#Orders', '#Preventive', '#Corrective', '#Planned', 'Ratio'],
              split,
            ),
          ],
        ),
        wfCard(
          'What constrains the window',
          'roster, ventilation and ground — the reasons a machine cannot simply be taken at will',
          [
            wfTable(['Site', 'Roster', 'Ventilation', 'Ground'], si),
            wfNote([
              nt('Full site conditions are on each '),
              nlink('mine site page', navShow('section', 'sites')),
              nt('.'),
            ]),
          ],
        ),
      ];
    },
    also: [also('Job cards', 'jobcards'), also('Mine sites', 'section', 'sites')],
  },

  {
    id: 'ready',
    mod: 'promise',
    name: 'Promise & readiness',
    tag: 'do not commit a date you cannot keep',
    purpose:
      'Before the window is offered to the customer, four things have to be true: the parts are on site or landing, a certified technician is on swing, the tooling is free and the machine can be accessed. This step scores each planned job on all four and holds back the ones that would slip.',
    kpis: (ctx) => {
      const low = ctx.pack.inventory.filter(
        (i) => i.qty_on_hand < i.min_level && i.location.includes('site'),
      ).length;
      return [
        kpi('Planned jobs', ctx.derived.jobs.length, 'being readied'),
        kpi('Parts risk', low, 'site lines below minimum'),
        kpi(
          'Technicians available',
          `${ctx.pack.roster.rows.filter((r) => r[6] === 'Yes').length}/${ctx.pack.roster.rows.length}`,
          'on swing this week',
        ),
        kpi(
          'Approvals waiting',
          ctx.workOrders.filter((w) => w.status.startsWith('Draft')).length +
            ctx.pack.orders.filter((o) => /agent/i.test(o.status)).length,
          'drafts for a person',
        ),
      ];
    },
    blocks: (ctx) => {
      const tick = (ok: boolean, text: string): WorkflowCell => ({ kind: 'check', ok, text });
      const J = ctx.derived.jobs
        .filter((j): j is CaseJob & { machine: string } => Boolean(j.machine))
        .slice(0, 12)
        .map((j): readonly WorkflowCell[] => {
          const f = ctx.machineById[j.machine];
          const mine = f ? f.mine : '';
          const store = ctx.pack.inventory.filter((i) => i.location === `${mine} site store`);
          const short = store.filter((i) => i.qty_on_hand < i.min_level).length;
          const tech = ctx.pack.tapp_techs.some((t) => t.base === mine);
          const roster = ctx.pack.roster.rows.some(
            (r) => r[2] === (f ? ctx.mines[mine].name : '') && r[6] === 'Yes',
          );
          const access = f ? ctx.mines[mine].data_mode === 'Live' : false;
          const rdy = short === 0 && tech && roster;
          return [
            mono(j.id),
            mcell(j.machine),
            f ? ctx.mines[mine].name : '—',
            tick(short === 0, short === 0 ? 'parts held' : `${short} lines short`),
            tick(tech, tech ? 'tech on site' : 'covered from DC'),
            tick(roster, roster ? 'on swing' : 'swing change'),
            tick(access, access ? 'live link' : 'shift-end only'),
            {
              kind: 'pill',
              text: rdy ? 'Ready to promise' : 'Hold',
              tone: rdy ? 'good' : 'warn',
            },
          ];
        });
      const ap: readonly (readonly WorkflowCell[])[] = [
        ...ctx.workOrders
          .filter((w) => w.status.startsWith('Draft'))
          .map((w): readonly WorkflowCell[] => [
            mono(w.wo_id),
            mcell(w.machine_id),
            ctx.mines[w.mine].name,
            (w.description ?? '').slice(0, 64),
            `A$${w.total_direct_cost_aud.toLocaleString()}`,
            'Work order agent',
          ]),
        ...ctx.pack.orders
          .filter((o) => /agent/i.test(o.status))
          .slice(0, 6)
          .map((o): readonly WorkflowCell[] => [
            mono(o.order_no),
            o.machine_id ? mcell(o.machine_id) : '—',
            ctx.mines[o.mine].name,
            `${o.part_no} × ${o.qty ?? 1}`,
            '—',
            'Parts & logistics agent',
          ]),
      ];
      return [
        wfCard(
          'Readiness by job',
          'four checks before a window is offered — any cross and the promise is held',
          [
            wfTable(
              ['Case', 'Machine', 'Site', 'Parts', 'People', 'Roster', 'Access', 'Verdict'],
              J,
            ),
          ],
        ),
        wfCard(
          'Waiting on a person',
          'nothing below has touched a machine, a schedule or the order book',
          [
            wfTable(['Item', 'Machine', 'Site', 'What it does', '#Value', 'Drafted by'], ap),
            wfNote([
              nlink('Open the approvals queue', navShow('approvals')),
              nt(' to action them.'),
            ]),
          ],
        ),
      ];
    },
    also: [also('Approvals', 'approvals'), also('Stock & replenishment', 'wf', 'stock')],
  },

  /* ---------- 07 Parts & Supply ---------- */
  {
    id: 'pipe',
    mod: 'parts',
    name: 'Pipeline & expediting',
    tag: 'the part lands inside the window, from the cheapest place it can',
    purpose:
      'Every requisition from requisition to receipt, with the need-by date sitting next to the ETA. Where the two do not meet, the step shows what the escalation costs — a transfer from another site, an air freight, or a window moved.',
    kpis: (ctx) => [
      kpi(
        'Orders in flight',
        ctx.pack.orders.filter((o) => !/Delivered/i.test(o.status)).length,
        'not yet received',
      ),
      kpi(
        'Agent drafts',
        ctx.pack.orders.filter((o) => /agent/i.test(o.status)).length,
        'waiting for approval',
      ),
      kpi(
        'On air freight',
        ctx.pack.orders.filter((o) => /air/i.test(o.status)).length,
        'escalated to meet a window',
      ),
      kpi(
        'Mean lead time',
        `${Math.round(mean(ctx.pack.parts.map((p) => p.lead_time_days)))} d`,
        'catalogue average',
      ),
    ],
    blocks: (ctx) => {
      const rows = ctx.pack.orders
        .slice()
        .sort((a, b) => b.order_date.localeCompare(a.order_date))
        .slice(0, 14)
        .map((o): readonly WorkflowCell[] => {
          const p = ctx.pack.parts.find((x) => x.part_no === o.part_no);
          return [
            mono(o.order_no),
            o.order_date,
            mono(o.part_no),
            (p?.description ?? '').slice(0, 42),
            String(o.qty ?? 1),
            o.machine_id ? mcell(o.machine_id) : '—',
            ctx.mines[o.mine].name,
            o.status.slice(0, 40),
            o.linked_wo ? mono(o.linked_wo) : '—',
          ];
        });
      const fr = ctx.mineOrder.map((m): readonly WorkflowCell[] => {
        const r = ctx.pack.rates[m];
        return [
          ctx.mines[m].name,
          r.dc,
          `${r.freight_days} d road`,
          ctx.mines[m].state,
          String(ctx.pack.inventory.filter((i) => i.location === `${m} site store`).length),
          String(ctx.pack.inventory.filter((i) => i.location === r.dc).length),
        ];
      });
      const crit = ctx.pack.parts
        .filter((p) => p.criticality === 'Critical')
        .sort((a, b) => b.lead_time_days - a.lead_time_days)
        .slice(0, 10)
        .map((p): readonly WorkflowCell[] => [
          mono(p.part_no),
          p.description.slice(0, 48),
          p.system,
          `A$${p.list_price_aud.toLocaleString()}`,
          `${p.lead_time_days} d`,
          p.fits,
        ]);
      return [
        wfCard('Orders in flight', 'newest first, each tied back to the work order that needs it', [
          wfTable(
            ['Order', 'Raised', 'Part', 'Description', '#Qty', 'Machine', 'Site', 'Status', 'Work order'],
            rows,
          ),
        ]),
        wfCard(
          'How parts reach each site',
          'the distribution centre, the road time and what is already held locally',
          [
            wfTable(
              ['Site', 'Distribution centre', 'Road time', 'State', '#Site store lines', '#DC lines'],
              fr,
            ),
          ],
        ),
        wfCard(
          'Critical parts with the longest lead',
          'the lines that decide whether a window can be met at all',
          [
            wfTable(['Part', 'Description', 'System', '#List price', '#Lead time', 'Fits'], crit),
            wfNote([
              nt('Stock positions and reorder proposals are in '),
              nlink('Stock & replenishment', navShow('wf', 'stock')),
              nt('.'),
            ]),
          ],
        ),
      ];
    },
    also: [also('Parts pipeline', 'partsflow'), also('Stock & replenishment', 'wf', 'stock')],
  },

  {
    id: 'stock',
    mod: 'parts',
    name: 'Stock & replenishment',
    tag: 'what is held, where, and what has to be topped up',
    purpose:
      'The position across every site store and distribution centre: what is below minimum, what the gap costs to close, and whether it is cheaper to transfer between sites than to order. Holding stock is capital; not holding it is downtime.',
    kpis: (ctx) => [
      kpi('Stock lines', ctx.pack.inventory.length, 'across all locations'),
      kpi(
        'Below minimum',
        ctx.pack.inventory.filter((i) => i.qty_on_hand < i.min_level).length,
        'lines to replenish',
      ),
      kpi(
        'Site stores',
        [...new Set(ctx.pack.inventory.map((i) => i.location))].filter((l) => l.includes('site'))
          .length,
        'on the mines',
      ),
      kpi(
        'Distribution centres',
        new Set(Object.values(ctx.pack.rates).map((r) => r.dc)).size,
        'holding the depth',
      ),
    ],
    blocks: (ctx) => {
      const CRIT: Readonly<Record<string, number>> = { Critical: 0, Standard: 1, Consumable: 2 };
      const low = ctx.pack.inventory
        .filter((i) => i.qty_on_hand < i.min_level)
        .map((i) => ({ i, p: ctx.pack.parts.find((x) => x.part_no === i.part_no) }))
        .sort(
          (a, b) =>
            (CRIT[a.p?.criticality ?? ''] ?? 3) - (CRIT[b.p?.criticality ?? ''] ?? 3) ||
            b.i.min_level - b.i.qty_on_hand - (a.i.min_level - a.i.qty_on_hand) ||
            (b.p?.lead_time_days ?? 0) - (a.p?.lead_time_days ?? 0),
        )
        .slice(0, 14)
        .map(({ i, p }): readonly WorkflowCell[] => [
          i.location,
          mono(i.part_no),
          (p?.description ?? '').slice(0, 44),
          String(i.qty_on_hand),
          String(i.min_level),
          { kind: 'emphasis', text: String(i.min_level - i.qty_on_hand), tone: 'warn' },
          dash(p?.criticality),
          `${p?.lead_time_days ?? '—'} d`,
        ]);
      const byLoc = new Map<string, { n: number; q: number; low: number }>();
      for (const i of ctx.pack.inventory) {
        const e = byLoc.get(i.location) ?? { n: 0, q: 0, low: 0 };
        e.n += 1;
        e.q += i.qty_on_hand;
        if (i.qty_on_hand < i.min_level) e.low += 1;
        byLoc.set(i.location, e);
      }
      const lrows = [...byLoc]
        .sort((a, b) => b[1].low - a[1].low)
        .map(([k, v]): readonly WorkflowCell[] => [
          k,
          String(v.n),
          String(v.q),
          v.low ? { kind: 'emphasis', text: String(v.low), tone: 'warn' } : '0',
          `${Math.round((1 - v.low / v.n) * 100)}%`,
        ]);
      const prop = ctx.derived.predictedUsage
        .filter((r) => r.gap > 0)
        .slice(0, 8)
        .map((r): readonly WorkflowCell[] => [
          mono(r.part_no),
          ctx.mines[r.mine].name,
          String(Math.ceil(r.demand)),
          String(r.store ?? 0),
          String(r.gap),
          r.prop ?? '',
        ]);
      return [
        wfCard(
          'Below minimum now',
          'ordered by location — the criticality column decides what gets expedited',
          [
            wfTable(
              ['Location', 'Part', 'Description', '#On hand', '#Minimum', '#Gap', 'Criticality', 'Lead time'],
              low,
            ),
          ],
        ),
        wfCard(
          'Holdings by location',
          'service level is the share of lines at or above their minimum',
          [
            wfTable(
              ['Location', '#Lines', '#Units held', '#Below minimum', '#Service level'],
              lrows,
            ),
          ],
        ),
        wfCard(
          'Replenishment proposals',
          'drafted against predicted demand, cheapest source first',
          [
            wfTable(['Part', 'Site', '#Demand', '#On site', '#Gap', 'Proposal'], prop),
            wfNote([
              nt('Nothing here is ordered until a person approves it in '),
              nlink('Approvals', navShow('approvals')),
              nt('.'),
            ]),
          ],
        ),
      ];
    },
    also: [also('Inventory control', 'partsflow', 'inventory'), also('Approvals', 'approvals')],
  },

  {
    id: 'book',
    mod: 'parts',
    name: 'Parts book & supersessions',
    tag: 'the right number for the right serial range',
    purpose:
      'The catalogue behind every requisition: assemblies broken down by section, quantity per assembly, criticality, list price and lead time, with the serial ranges that decide which number is actually correct for the machine in front of you.',
    kpis: (ctx) => [
      kpi('Catalogue lines', ctx.pack.catbase_bom.length, 'in the parts book'),
      kpi('Section entries', ctx.pack.catbase_sections.length, 'illustrated sections'),
      kpi(
        'Critical lines',
        ctx.pack.catbase_bom.filter((b) => b.criticality === 'Critical').length,
        'hold-at-site candidates',
      ),
      kpi(
        'Model families',
        new Set(ctx.pack.catbase_bom.map((b) => b.model_family)).size,
        'covered',
      ),
    ],
    blocks: (ctx) => {
      const rows = ctx.pack.catbase_bom.slice(0, 14).map((b): readonly WorkflowCell[] => [
        b.model_family,
        b.section,
        mono(b.part_no),
        b.description.slice(0, 46),
        String(b.qty_per_assembly),
        b.criticality,
        `A$${b.list_price_aud.toLocaleString()}`,
        `${b.lead_time_days} d`,
        b.serial_range,
      ]);
      const sec = ctx.pack.catbase_sections.slice(0, 12).map((s): readonly WorkflowCell[] => [
        s.model,
        s.section,
        s.section_title,
        mono(s.part_no),
        s.description.slice(0, 44),
        String(s.qty_per_assembly),
        dash(s.note),
      ]);
      const byModel = new Map<string, Set<string>>();
      for (const s of ctx.pack.catbase_sections) {
        const e = byModel.get(s.model) ?? new Set<string>();
        e.add(s.section_title);
        byModel.set(s.model, e);
      }
      const mrows = [...byModel].map(([k, v]): readonly WorkflowCell[] => [
        k,
        String(ctx.fleet.filter((f) => f.model === k).length),
        String(v.size),
        [...v].slice(0, 4).join(' · '),
      ]);
      return [
        wfCard(
          'Parts book',
          'one row per assembly line, with the serial range that makes it the right number',
          [
            wfTable(
              ['Family', 'Section', 'Part', 'Description', '#Qty/assy', 'Criticality', '#List', '#Lead', 'Serial range'],
              rows,
            ),
          ],
        ),
        wfCard(
          'Illustrated sections',
          'the section index the technician works from in the field',
          [
            wfTable(
              ['Model', '#Section', 'Title', 'Part', 'Description', '#Qty', 'Note'],
              sec,
            ),
          ],
        ),
        wfCard('Coverage by model', 'how much of the book exists for each machine in the base', [
          wfTable(['Model', '#Machines', '#Sections', 'Sections'], mrows),
          wfNote([
            nt('The source files sit in '),
            nlink('Data', navShow('data')),
            nt(' under the CatBase connector.'),
          ]),
        ]),
      ];
    },
    also: [also('BOM lookup', 'partsflow', 'bom'), also('Data', 'data')],
  },

  /* ---------- 08 People & Resources ---------- */
  {
    id: 'crew',
    mod: 'capacity',
    name: 'Capacity & utilisation',
    tag: 'the right person, available and close enough',
    purpose:
      'Capacity is the constraint the plan runs into first. This step holds who is on swing, how their time is actually going, where they are based and how much of the network a change of one person moves.',
    kpis: (ctx) => [
      kpi('Technicians', ctx.pack.tapp_techs.length, 'in the region'),
      kpi(
        'On site now',
        ctx.pack.tapp_techs.filter((t) => t.status === 'On site').length,
        `of ${ctx.pack.tapp_techs.length}`,
      ),
      kpi('Mean utilisation', `${ctx.derived.meanUtil}%`, 'job and PM hours against available'),
      kpi(
        'On swing',
        ctx.pack.roster.rows.filter((r) => r[6] === 'Yes').length,
        'available this week',
      ),
    ],
    blocks: (ctx) => {
      const rows = ctx.pack.tapp_techs.map((t): readonly WorkflowCell[] => {
        const s = ctx.helpers.techStats(t.technician);
        const ts = ctx.pack.timesheets.filter((x) => x.technician === t.technician);
        return [
          t.technician,
          ctx.mines[t.base].name,
          { kind: 'pill', text: t.status, tone: t.status === 'On site' ? 'good' : 'mute' },
          String(s.done ?? '—'),
          `${s.util}%`,
          s.fft != null ? `${s.fft}%` : '—',
          s.cssAvg ? s.cssAvg.toFixed(1) : '—',
          `${Math.round(ts.reduce((a, x) => a + x.job_hours, 0))} h`,
        ];
      });
      const r = ctx.pack.roster.rows.map((x): readonly WorkflowCell[] => [
        x[0],
        x[1],
        x[2],
        x[3],
        x[4],
        x[5],
        { kind: 'pill', text: x[6], tone: x[6] === 'Yes' ? 'good' : 'mute' },
      ]);
      const bySite = ctx.mineOrder.map((m): readonly WorkflowCell[] => {
        const based = ctx.pack.tapp_techs.filter((t) => t.base === m).length;
        const fs = ctx.fleet.filter((f) => f.mine === m).length;
        return [
          ctx.mines[m].name,
          String(fs),
          String(based),
          based
            ? `${(fs / based).toFixed(0)} machines / technician`
            : `covered from ${ctx.pack.rates[m].dc}`,
          `${ctx.pack.rates[m].freight_days} d from DC`,
        ];
      });
      return [
        wfCard(
          'Technicians',
          'utilisation is job and PM hours against available hours; first-time fix counts a repeat inside sixty days as a miss',
          [
            wfTable(
              ['Technician', 'Base', 'Status', '#Jobs done', '#Utilisation', '#First-time fix', '#Customer score', '#Hours'],
              rows,
            ),
          ],
        ),
        wfCard(
          'Roster cover',
          'swings, availability and the customer preferences the planner has to work around',
          [
            wfTable(
              ['Technician', 'Role', 'Base site', 'Roster', 'Swing start', 'Swing end', 'Available'],
              r,
            ),
          ],
        ),
        wfCard('Cover by site', 'where the thin spots are', [
          wfTable(['Site', '#Machines', '#Based here', 'Ratio', 'Support distance'], bySite),
          wfNote([
            nt('Certification and who can legally do what is in '),
            nlink('Skills & certification', navShow('wf', 'skills')),
            nt('.'),
          ]),
        ]),
      ];
    },
    also: [also('Technicians', 'techs'), also('Skills & certification', 'wf', 'skills')],
  },

  {
    id: 'skills',
    mod: 'capacity',
    name: 'Skills & certification',
    tag: 'certified, current, and covered when they are not',
    purpose:
      'A technician who is available but not current cannot do the job. This step tracks certificates and expiry against the work each person is scheduled for, and who the escalation expert is when the skill is not on site at all.',
    kpis: (ctx) => [
      kpi('Certificates', ctx.pack.lms.length, 'on file'),
      kpi(
        'Expiring or expired',
        ctx.pack.lms.filter((r) => r.status !== 'Current').length,
        'need renewing',
      ),
      kpi('Courses', new Set(ctx.pack.lms.map((r) => r.course)).size, 'in the catalogue'),
      kpi(
        'Remote experts',
        new Set(ctx.pack.xreach_sessions.map((s) => s.expert)).size,
        'on the escalation network',
      ),
    ],
    blocks: (ctx) => {
      const exp = ctx.pack.lms
        .filter((r) => r.status !== 'Current')
        .slice(0, 14)
        .map((r): readonly WorkflowCell[] => [
          r.person,
          r.org,
          r.role,
          r.course,
          r.completed,
          r.expires,
          {
            kind: 'pill',
            text: r.status,
            tone: /Expired/i.test(r.status) ? 'vpoor' : 'warn',
          },
        ]);
      const sk = ctx.pack.tapp_techs.map((t): readonly WorkflowCell[] => [
        t.technician,
        ctx.mines[t.base].name,
        t.skills.join(' '),
        String(ctx.pack.lms.filter((r) => r.person === t.technician).length),
        String(
          ctx.pack.lms.filter((r) => r.person === t.technician && r.status !== 'Current').length ||
            '—',
        ),
      ]);
      const ex = new Map<string, { n: number; org: string; t: Set<string> }>();
      for (const s of ctx.pack.xreach_sessions) {
        const e = ex.get(s.expert) ?? { n: 0, org: s.expert_org, t: new Set<string>() };
        e.n += 1;
        e.t.add(s.topic);
        ex.set(s.expert, e);
      }
      const erows = [...ex]
        .sort((a, b) => b[1].n - a[1].n)
        .map(([k, v]): readonly WorkflowCell[] => [
          k,
          v.org,
          String(v.n),
          [...v.t].slice(0, 3).join(' · '),
        ]);
      return [
        wfCard(
          'Certificates expiring or expired',
          'the ones that stop a person being scheduled',
          [
            wfTable(
              ['Person', 'Organisation', 'Role', 'Course', 'Completed', 'Expires', 'Status'],
              exp,
            ),
          ],
        ),
        wfCard('Skill matrix', 'what each technician is signed off to work on', [
          wfTable(['Technician', 'Base', 'Skills', '#Certificates', '#Not current'], sk),
        ]),
        wfCard('Escalation cover', 'who is behind the field when the skill is not on site', [
          wfTable(['Expert', 'Organisation', '#Sessions', 'Topics'], erows),
          wfNote([
            nt('Sessions themselves are in '),
            nlink('Remote diagnosis', navShow('wf', 'remote4')),
            nt('.'),
          ]),
        ]),
      ];
    },
    also: [also('Remote support', 'remote'), also('Technicians', 'techs')],
  },

  /* ---------- 09 Execution & Support ---------- */
  {
    id: 'pack',
    mod: 'execute',
    name: 'Work package execution',
    tag: 'everything the technician needs, in their hand',
    purpose:
      'The work package is what turns a plan into a repeatable job: the steps, the torque values, the isolation points, the parts kit and the sign-off, all on the device in front of the technician, with the expert one tap away.',
    kpis: (ctx) => [
      kpi(
        'Open packages',
        ctx.pack.tapp_jobs.filter((j) => j.status !== 'Completed').length,
        'in the field now',
      ),
      kpi(
        'Completed',
        ctx.pack.tapp_jobs.filter((j) => j.status === 'Completed').length,
        'last ten weeks',
      ),
      kpi('Procedures', ctx.pack.docs.length, 'attached to job types'),
      kpi('Bulletins', ctx.pack.bulletins.length, 'in force on the packages'),
    ],
    blocks: (ctx) => {
      const open = ctx.pack.tapp_jobs
        .filter((j) => j.status !== 'Completed')
        .slice(0, 12)
        .map((j): readonly WorkflowCell[] => [
          mono(j.job_id),
          mono(dash(j.wo_ref)),
          j.technician,
          j.machine ? mcell(j.machine) : '—',
          j.site,
          j.job_type,
          j.status,
          j.on_site ? j.on_site.replace('T', ' ') : '—',
        ]);
      const bl = ctx.pack.bulletins.slice(0, 10).map((b): readonly WorkflowCell[] => [
        mono(b.id),
        b.title,
        { kind: 'link', label: 'open', nav: navShow('data') },
      ]);
      return [
        wfCard('Packages in the field', 'what is open right now, and who has it', [
          wfTable(
            ['Job', 'Work order', 'Technician', 'Machine', 'Site', 'Type', 'Status', 'On site'],
            open,
          ),
          wfNote([
            nlink('Open the job cards', navShow('jobcards')),
            nt(' for the full package on any one of these.'),
          ]),
        ]),
        wfCard(
          'What is in a package',
          'five things, every time — this is what makes the job repeatable across six sites',
          [
            {
              kind: 'routes',
              routes: [
                {
                  title: 'Steps and torque values',
                  text: 'The job plan from the Maximo PM, with the correct figures for that serial range out of the parts book.',
                  link: { label: 'Parts book', nav: navShow('wf', 'book') },
                },
                {
                  title: 'Isolation and permits',
                  text: 'The LOTO procedure for the machine class and the site’s own permit rules, both acknowledged before work starts.',
                  link: { label: 'Procedures', nav: navShow('data') },
                },
                {
                  title: 'Parts kit',
                  text: 'Picked against the package, reserved in the store, with the core return already raised for exchange units.',
                  link: { label: 'Stock position', nav: navShow('wf', 'stock') },
                },
                {
                  title: 'Expert on tap',
                  text: 'A remote session opens from inside the package without leaving the job.',
                  link: { label: 'Remote diagnosis', nav: navShow('wf', 'remote4') },
                },
                {
                  title: 'Sign-off',
                  text: 'Photographs, readings and the technician’s sign-off close the package and start the closeout in 10.',
                  link: { label: 'Job costing', nav: navShow('wf', 'cost') },
                },
              ],
            },
          ],
        ),
        wfCard(
          'Bulletins in force',
          'applied to the package automatically when the machine matches',
          [wfTable(['Bulletin', 'Title', ''], bl)],
        ),
      ];
    },
    also: [also('Job cards', 'jobcards'), also('Remote support', 'remote')],
  },

  {
    id: 'onsite',
    mod: 'execute',
    name: 'On-site time capture',
    tag: 'what actually happened, hour by hour',
    purpose:
      'The honest record of execution: hours on the tools against travel and standby, where the shift went and why, and the delay reasons that keep showing up. This is the data every cost and every future estimate is built on.',
    kpis: (ctx) => {
      const ts = ctx.pack.timesheets.filter((t) => !t.day_off);
      const job = ts.reduce((a, t) => a + t.job_hours, 0);
      const tr = ts.reduce((a, t) => a + t.travel_hours, 0);
      const sb = ts.reduce((a, t) => a + t.standby_hours, 0);
      return [
        kpi('Hours on tools', Math.round(job), 'booked to jobs'),
        kpi(
          'Travel',
          `${Math.round(tr)} h`,
          `${Math.round((tr / (job + tr + sb)) * 100)}% of booked time`,
        ),
        kpi('Standby', `${Math.round(sb)} h`, 'waiting, not working'),
        kpi('Days recorded', ts.length, 'technician-days'),
      ];
    },
    blocks: (ctx) => {
      const by = new Map<
        string,
        { d: number; j: number; p: number; tr: number; sb: number; off: number }
      >();
      for (const t of ctx.pack.timesheets) {
        const o = by.get(t.technician) ?? { d: 0, j: 0, p: 0, tr: 0, sb: 0, off: 0 };
        by.set(t.technician, o);
        if (t.day_off) {
          o.off += 1;
          continue;
        }
        o.d += 1;
        o.j += t.job_hours;
        o.p += t.pm_support_hours;
        o.tr += t.travel_hours;
        o.sb += t.standby_hours;
      }
      const rows = [...by].map(([k, v]): readonly WorkflowCell[] => [
        k,
        String(v.d),
        v.j.toFixed(1),
        v.p.toFixed(1),
        v.tr.toFixed(1),
        v.sb.toFixed(1),
        String(v.off),
        `${Math.round(((v.j + v.p) / (v.d * 12)) * 100)}%`,
      ]);
      const del = new Map<string, { n: number; m: Set<string> }>();
      for (const s of ctx.shiftDays) {
        if (!s.delays) continue;
        for (const d of s.delays
          .split(';')
          .map((x) => x.trim())
          .filter(Boolean)) {
          const e = del.get(d) ?? { n: 0, m: new Set<string>() };
          e.n += 1;
          e.m.add(ctx.machineById[s.machine_id]?.mine ?? '—');
          del.set(d, e);
        }
      }
      const ranked = [...del].sort((a, b) => b[1].n - a[1].n);
      const drows = ranked.slice(0, 10).map(([k, v]): readonly WorkflowCell[] => [
        mono(k),
        String(v.n),
        [...v.m].map((m) => ctx.mines[m]?.name ?? m).join(' · '),
      ]);
      const bars: readonly WorkflowBar[] = ranked.slice(0, 6).map(([k, v]) => ({
        label: k,
        value: v.n,
        text: `${v.n} shifts`,
      }));
      return [
        wfCard(
          'Time captured by technician',
          'booked hours against the twelve-hour shift — standby is the number to attack',
          [
            wfTable(
              ['Technician', '#Days', '#Job h', '#PM support', '#Travel', '#Standby', '#Days off', '#Utilisation'],
              rows,
            ),
          ],
        ),
        wfCard('Why shifts lost time', 'delay codes off the shift record, across every site', [
          wfTable(['Delay code', '#Shifts', 'Sites'], drows),
          wfBars(bars),
        ]),
        wfCard(
          'Where it goes next',
          'the captured time is the input to costing and to the next estimate',
          [
            {
              kind: 'routes',
              routes: [
                {
                  title: 'Job costing',
                  text: 'Hours × the contract labour rate, plus parts and freight, closes the job.',
                  link: { label: 'Job costing', nav: navShow('wf', 'cost') },
                },
                {
                  title: 'Next estimate',
                  text: 'Actual hours for the same job type on the same class replace the standard.',
                  link: { label: 'Service plan', nav: navShow('wf', 'plan') },
                },
                {
                  title: 'Capacity',
                  text: 'Utilisation and standby feed the roster for the next swing.',
                  link: { label: 'Capacity', nav: navShow('wf', 'crew') },
                },
              ],
            },
          ],
        ),
      ];
    },
    also: [also('Technicians', 'techs'), also('Job cards', 'jobcards')],
  },

  /* ---------- 10 Closeout & Value ---------- */
  {
    id: 'cost',
    mod: 'closeout',
    name: 'Job costing & cost to serve',
    tag: 'every job closed properly is the next one made cheaper',
    purpose:
      'Closeout is where the money is found. Each job is costed to labour, parts, freight, external service and lost production, and rolled up to the machine, the site and the contract — so the conversation with the customer is about a number, not an impression.',
    kpis: (ctx) => [
      kpi('Ledger lines', '15,083', '18 months'),
      kpi(
        'Mean cost/op h',
        `A$${Math.round(mean(ctx.pack.cost_per_machine.map((c) => c.cost_per_op_hour_aud)))}`,
        'across the base',
      ),
      kpi(
        'Highest site',
        `A$${Math.max(
          ...ctx.mineOrder.map((m) => {
            const ids = ctx.fleet.filter((f) => f.mine === m).map((f) => f.machine_id);
            const c = ctx.pack.cost_per_machine.filter((x) => ids.includes(x.machine_id));
            return Math.round(mean(c.map((x) => x.cost_per_op_hour_aud)));
          }),
        )}`,
        'per operating hour',
      ),
      kpi(
        'Corrective share',
        `${Math.round(
          (ctx.workOrders.filter((w) => w.wo_type !== 'Preventive').length /
            ctx.workOrders.length) *
            100,
        )}%`,
        'of all work orders',
      ),
    ],
    blocks: (ctx) => {
      const rows = ctx.mineOrder.map((m): readonly WorkflowCell[] => {
        const ids = ctx.fleet.filter((f) => f.mine === m).map((f) => f.machine_id);
        const c = ctx.pack.cost_per_machine.filter((x) => ids.includes(x.machine_id));
        const tot = c.reduce((a, x) => a + x.amount_aud, 0);
        const cph = Math.round(mean(c.map((x) => x.cost_per_op_hour_aud)));
        return [
          ctx.mines[m].name,
          String(ids.length),
          `A$${Math.round(tot / 1000).toLocaleString()}k`,
          `A$${cph}`,
          `A$${ctx.mines[m].labour_rate}/h`,
          `${Math.round(
            (ctx.workOrders.filter((w) => w.mine === m && w.wo_type !== 'Preventive').length /
              ctx.workOrders.filter((w) => w.mine === m).length) *
              100,
          )}%`,
        ];
      });
      const cats = new Map<string, number>();
      const isPositional = (
        row: CostByCategoryRow,
      ): row is readonly [machineId: string, category: string, amount: number] =>
        Array.isArray(row);
      for (const r of ctx.pack.cost_by_machine_cat) {
        const k = isPositional(r) ? r[1] : r.category;
        const v = isPositional(r) ? r[2] : r.amount_aud;
        if (k == null) continue;
        cats.set(k, (cats.get(k) ?? 0) + (Number(v) || 0));
      }
      const bars: readonly WorkflowBar[] = [...cats]
        .sort((a, b) => b[1] - a[1])
        .map(([k, v]) => ({
          label: k,
          value: v,
          text: `A$${Math.round(v / 1000).toLocaleString()}k`,
        }));
      const top = ctx.pack.cost_per_machine
        .slice()
        .sort((a, b) => b.cost_per_op_hour_aud - a.cost_per_op_hour_aud)
        .slice(0, 10)
        .map((c): readonly WorkflowCell[] => {
          const f = ctx.machineById[c.machine_id];
          return [
            mcell(c.machine_id, true),
            f ? f.model : '—',
            f ? ctx.mines[f.mine].name : '—',
            `A$${c.cost_per_op_hour_aud}`,
            `A$${Math.round(c.amount_aud / 1000)}k`,
            `${c.op_hours_18m.toLocaleString()} h`,
          ];
        });
      return [
        wfCard(
          'Cost to serve by site',
          'all-in ledger cost over eighteen months, divided by operating hours',
          [
            wfTable(
              ['Site', '#Machines', '#18-month cost', '#Cost/op h', '#Labour rate', '#Corrective'],
              rows,
            ),
          ],
        ),
        wfCard(
          'Where the cost sits',
          'by category across the whole base — lost production is priced per class per mine, not guessed',
          [
            bars.length
              ? wfBars(bars)
              : wfNote([nt('Category breakdown is on the costs page.')]),
          ],
        ),
        wfCard(
          'Most expensive machines to run',
          'the ten worst cost per operating hour — each one is a case for a different plan, not a different mechanic',
          [
            wfTable(
              ['Machine', 'Model', 'Site', '#Cost/op h', '#18-month', '#Operating h'],
              top,
            ),
            wfNote([
              nt('Full ledger, energy and emissions are in '),
              nlink('Cost summary', navShow('costs')),
              nt('.'),
            ]),
          ],
        ),
      ];
    },
    also: [also('Cost summary', 'costs'), also('Closeout module', 'module', 'closeout')],
  },

  {
    id: 'outcome',
    mod: 'closeout',
    name: 'Customer outcome & CSS',
    tag: 'what the customer thought, in their words',
    purpose:
      'The last step and the first one: the survey goes out with the closeout, the scores are attributed to the job and the technician, and the comments are read — because the lowest-scoring dimension is usually the one nobody was measuring.',
    kpis: (ctx) => {
      const r = ctx.pack.css.rows;
      const avg = (i: 7 | 8 | 9 | 10): string =>
        (r.reduce((a, x) => a + x[i], 0) / r.length).toFixed(1);
      return [
        kpi('Responses', r.length, 'surveyed jobs'),
        kpi('Overall', avg(10), 'out of 5'),
        kpi('Lowest dimension', 'Reporting', `${avg(9)} — the one to fix`),
        kpi('Below 3.5', r.filter((x) => x[10] < 3.5).length, 'responses to follow up'),
      ];
    },
    blocks: (ctx) => {
      const r = ctx.pack.css.rows;
      const rows = r.slice(0, 12).map((x): readonly WorkflowCell[] => [
        mono(x[0]),
        x[2],
        x[3],
        x[4],
        x[5] ? mcell(x[5]) : '—',
        x[6],
        String(x[7]),
        String(x[8]),
        String(x[9]),
        {
          kind: 'emphasis',
          text: String(x[10]),
          tone: x[10] >= 4 ? 'good' : x[10] >= 3.5 ? 'fair' : 'vpoor',
        },
      ]);
      const dimensions: readonly (readonly [string, 7 | 8 | 9 | 10])[] = [
        ['Quality', 7],
        ['Responsiveness', 8],
        ['Reporting', 9],
        ['Overall', 10],
      ];
      const dims: readonly WorkflowBar[] = dimensions.map(([n, i]) => ({
        label: n,
        value: Number((r.reduce((a, x) => a + x[i], 0) / r.length).toFixed(2)),
        text: `${(r.reduce((a, x) => a + x[i], 0) / r.length).toFixed(1)} / 5`,
      }));
      const comments = r.filter((x) => x[11]).slice(0, 6);
      const commentBlock: WorkflowBlock = comments.length
        ? {
            kind: 'quotes',
            lines: comments.map((x) => ({
              site: x[2],
              contact: x[3],
              machine: x[5] || '—',
              comment: x[11],
            })),
          }
        : wfNote([nt('No written comments on the current set.')]);
      const byTech = new Map<string, { n: number; s: number }>();
      for (const x of r) {
        const e = byTech.get(x[4]) ?? { n: 0, s: 0 };
        e.n += 1;
        e.s += x[10];
        byTech.set(x[4], e);
      }
      const trows = [...byTech]
        .sort((a, b) => b[1].s / b[1].n - a[1].s / a[1].n)
        .map(([k, v]): readonly WorkflowCell[] => [k, String(v.n), (v.s / v.n).toFixed(1)]);
      return [
        wfCard(
          'Survey responses',
          'sent with the closeout, scored by the person who received the service',
          [
            wfTable(
              ['Job', 'Site', 'Contact', 'Technician', 'Machine', 'Date', '#Quality', '#Responsiveness', '#Reporting', '#Overall'],
              rows,
            ),
          ],
        ),
        wfCard(
          'Scores by dimension',
          'reporting is consistently the lowest — the work gets done, the telling does not',
          [wfBars(dims)],
        ),
        wfCard('What customers said', 'read in full, not summarised away', [
          commentBlock,
          wfTable(['Technician', '#Responses', '#Mean score'], trows),
          wfNote([
            nt('Customer pages and contacts are in '),
            nlink('Customers', navShow('customers')),
            nt('.'),
          ]),
        ]),
      ];
    },
    also: [also('Customers', 'customers'), also('Commercial opportunity loop', 'loops', 'commercial')],
  },
];

/** `WORKFLOW_STEPS` keyed by id — the demo's `WFS_BY` (line 3685). */
export const WORKFLOW_STEP_BY_ID: Readonly<Record<WorkflowStepId, WorkflowStep>> =
  Object.fromEntries(WORKFLOW_STEPS.map((s) => [s.id, s])) as Record<
    WorkflowStepId,
    WorkflowStep
  >;

/** The demo's `wfOf(modId)` (line 3686) — the steps inside one module, in order. */
export const workflowStepsForModule = (mod: ModuleId): readonly WorkflowStep[] =>
  WORKFLOW_STEPS.filter((s) => s.mod === mod);
